import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { 
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription 
} from '../../components/ui/drawer';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Pause, Play, CheckCircle2, ArrowLeft, Clock, Package, 
  AlertTriangle, MessageSquare, Camera, X, Send, ChevronUp, ChevronRight,
  XCircle, Wrench, Quote, Coffee, Snowflake, ListTodo, PhoneCall
} from 'lucide-react';
import BreakAlert from '../../components/BreakAlert';
// PausaFlutuante removido — confundia o utilizador
import ContagemScreen from '../../components/operador/ContagemScreen';
import CompleteTaskScreen from '../../components/operador/CompleteTaskScreen';
import TimerScreen from '../../components/operador/TimerScreen';
import TrocaPendenteOverlay from '../../components/operador/TrocaPendenteOverlay';
import BottomTabBar from '../../components/operador/BottomTabBar';
import AlertasNotificacao from '../../components/AlertasNotificacao';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Motivos de pausa base (sempre disponíveis) — Almoço em primeiro lugar
const MOTIVOS_PAUSA_BASE = [
  { id: 'almoco', label: 'Almoço', icon: '🍽️', requerContagem: false, pausaLegal: true },
  { id: 'wc', label: 'Casa de banho', icon: '🚻', requerContagem: false },
  { id: 'material', label: 'Aguardando material', icon: '📦', requerContagem: false },
  { id: 'tecnico', label: 'Problema técnico', icon: '🔧', requerContagem: false },
  { id: 'outro', label: 'Outro motivo', icon: '💬', requerContagem: false }
];

const TIPOS_ANOMALIA = [
  { id: 'defeito', label: 'Defeito de material', color: 'bg-terracotta' },
  { id: 'maquina', label: 'Problema de máquina', color: 'bg-sand' },
  { id: 'qualidade', label: 'Qualidade', color: 'bg-terracotta' },
  { id: 'outro', label: 'Outro', color: 'bg-stone-400' }
];

// Tipos de anomalia de produção (defeito vs arranjo)
const TIPOS_ANOMALIA_PRODUCAO = [
  { id: 'defeito', label: 'Defeito (Perda Total)', description: 'Peça não pode ser recuperada', color: 'bg-red-100 text-red-700 border-red-300', icon: '❌' },
  { id: 'arranjo', label: 'Precisa de Arranjo', description: 'Peça pode ser recuperada', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: '🔧' }
];

export default function ProducaoAtiva() {
  const { user: authUser } = useAuth();
  const { username } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Detectar modo de simulação
  const isSimulation = location.pathname.includes('/sim/');
  
  // Detectar se está dentro de iframe
  const isInIframe = window.self !== window.top;
  
  // Função de navegação que funciona dentro de iframes
  const safeNavigate = (path) => {
    if (isInIframe) {
      // Dentro de iframe, usar navegação directa para evitar conflitos
      window.location.href = path;
    } else {
      navigate(path);
    }
  };
  
  // Estado para user (real ou simulado)
  // Em modo simulação, começar com null para forçar carregamento do operador correto
  const [user, setUser] = useState(isSimulation ? null : authUser);
  
  // Tipos de anomalias carregados do backend
  const [tiposAnomaliaBackend, setTiposAnomaliaBackend] = useState([]);
  
  // Configuração de horários e pausas legais
  const [configuracao, setConfiguracao] = useState(null);
  const [pausasLegais, setPausasLegais] = useState([]);
  
  // ID da operação pendente para descongelar (quando já existe uma operação ativa)
  const [pendingDescongelarId, setPendingDescongelarId] = useState(null);
  
  // Pausas programadas (alertas automáticos)
  const [showPausaProgramadaDialog, setShowPausaProgramadaDialog] = useState(false);
  const [pausaProgramadaAtual, setPausaProgramadaAtual] = useState(null); // { nome, horaFim }
  const [emPausaProgramada, setEmPausaProgramada] = useState(false); // Para distinguir de pausas manuais
  
  const [producaoData, setProducaoData] = useState(null);
  const [trocaPendente, setTrocaPendente] = useState(null); // Alerta de troca de operação pelo gestor
  const [submittingTroca, setSubmittingTroca] = useState(false);
  const [tempoDecorrido, setTempoDecorrido] = useState(() => {
    // Usar timestamp local do inicio para timer instantâneo (antes da API responder)
    const inicioLocal = sessionStorage.getItem('producao_inicio_local');
    if (inicioLocal) {
      return Math.max(0, Math.floor((Date.now() - new Date(inicioLocal).getTime()) / 1000));
    }
    return 0;
  });
  const [tempoPausaAtual, setTempoPausaAtual] = useState(0);
  const [tempoExtra, setTempoExtra] = useState(0); // Tempo trabalhado fora do horário normal
  const [foraHorario, setForaHorario] = useState(false); // Indica se está fora do horário normal
  const [emPausa, setEmPausa] = useState(false);
  const [ultimoMotivoPausa, setUltimoMotivoPausa] = useState(''); // Para detectar pausa de almoço
  
  // Drawers
  const [showPausaDrawer, setShowPausaDrawer] = useState(false);
  const [showConcluirDrawer, setShowConcluirDrawer] = useState(false);
  const [showParcialDrawer, setShowParcialDrawer] = useState(false);
  const [showOcorrenciaDrawer, setShowOcorrenciaDrawer] = useState(false);
  const [showRevisaoDrawer, setShowRevisaoDrawer] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showAnomaliasDrawer, setShowAnomaliasDrawer] = useState(false);
  const [showConfirmacaoParcial, setShowConfirmacaoParcial] = useState(false); // Confirmação quando quantidade < total
  const [showTerminarDiaDialog, setShowTerminarDiaDialog] = useState(false); // Dialog para terminar o dia
  const [showCongelarDialog, setShowCongelarDialog] = useState(false); // Dialog para congelar operação
  const [showAvisoCongeladaDialog, setShowAvisoCongeladaDialog] = useState(false); // Aviso ao sair com congelada
  const [pendingNavigationTab, setPendingNavigationTab] = useState(null); // Tab pendente após aviso
  const [showOperacoesCongeladas, setShowOperacoesCongeladas] = useState(false); // Drawer de operações congeladas
  const [operacoesCongeladas, setOperacoesCongeladas] = useState([]); // Lista de operações congeladas do operador
  const [tarefaPontualCongelada, setTarefaPontualCongelada] = useState(null); // Tarefa pontual congelada (constrange congelar op)
  const [veioDeCongelada, setVeioDeCongelada] = useState(false); // Indica se a produção atual veio de uma descongelação
  
  // Estados para alerta de horas extras / fim de dia automático
  const [horasExtrasConfirmado, setHorasExtrasConfirmado] = useState(false); // Flag para não enviar notificação repetidamente
  
  // Form states
  const [motivoPausa, setMotivoPausa] = useState('');
  const [contagemPausa, setContagemPausa] = useState(''); // Contagem obrigatória para almoço/fim do dia
  const [quantidadeProduzida, setQuantidadeProduzida] = useState('');
  const [motivoParcial, setMotivoParcial] = useState('');
  const [tipoOcorrencia, setTipoOcorrencia] = useState('');
  const [descricaoOcorrencia, setDescricaoOcorrencia] = useState('');
  const [quantidadeAfetada, setQuantidadeAfetada] = useState('');
  const [motivoRevisao, setMotivoRevisao] = useState('');
  
  // Estados para o novo drawer de conclusão/parcial
  const [modoConclusao, setModoConclusao] = useState(''); // 'parcial' ou 'concluir'
  const [quantidadeDesvio, setQuantidadeDesvio] = useState(0); // Diferença entre esperado e real (só para concluir)
  const [quantidadeRetificada, setQuantidadeRetificada] = useState(''); // Nova quantidade do lote (só para operações de contagem)
  const [contagemAtualContagem, setContagemAtualContagem] = useState(0); // Contagem atual vinda do ContagemScreen
  
  // Estados para anomalias de produção
  const [anomaliasProducao, setAnomaliasProducao] = useState([]);
  const [novaAnomalia, setNovaAnomalia] = useState({
    tipo_anomalia_id: '', // ID do tipo de anomalia do backend
    classificacao: '', // 'defeito' ou 'arranjo'
    quantidade: '',
    observacoes: '',
    fotos: []
  });
  const [concluirComAnomalias, setConcluirComAnomalias] = useState(false);
  const [quantidadeAnomalias, setQuantidadeAnomalias] = useState(0);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  
  // Estado para quantidade disponível em tempo real (quando 2+ operadores trabalham no mesmo lote)
  const [quantidadeDisponivelRealTime, setQuantidadeDisponivelRealTime] = useState(null);
  const [loadingQuantidadeDisponivel, setLoadingQuantidadeDisponivel] = useState(false);
  
  // Estados para próximas operações após concluir
  const [showProximasOperacoes, setShowProximasOperacoes] = useState(false);
  const [proximasOperacoes, setProximasOperacoes] = useState([]);
  const [ofConcluida, setOfConcluida] = useState(null); // Guardar dados da OF após concluir
  const [dadosConclusaoAnterior, setDadosConclusaoAnterior] = useState(null); // Guardar dados completos para verificação posterior
  const fotoInputRef = useRef(null);
  const descongelarProcessedRef = useRef(null); // Evitar dupla execução do descongelar
  const operadorConcluiuRef = useRef(false); // Track quando o operador conclui a sua própria operação
  const emPausaRef = useRef(false); // Sincroniza emPausa para closures do polling (pausas legais)
  const emPausaProgramadaRef = useRef(false);
  
  const [loading, setLoading] = useState(false);
  
  // Estado para modal simplificado de quantidade (tarefas pontuais)
  const [pontualQty, setPontualQty] = useState('');
  
  // Estado para frase motivacional de fim de tarefa
  const [fraseFimTarefa, setFraseFimTarefa] = useState('');
  const [showSucessoDialog, setShowSucessoDialog] = useState(false);

  // Carregar frases motivacionais de fim de tarefa
  const carregarFraseFimTarefa = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/configuracoes/frases-motivacionais/ativas?contexto=fim_tarefa`);
      if (response.data && response.data.length > 0) {
        const fraseAleatoria = response.data[Math.floor(Math.random() * response.data.length)];
        setFraseFimTarefa(fraseAleatoria.texto);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.log('Sem frases motivacionais disponíveis');
    }
  }, []);

  // Manter refs sincronizados com state — usado pelo polling de pausas legais (closures estáveis)
  useEffect(() => { emPausaRef.current = emPausa; }, [emPausa]);
  useEffect(() => { emPausaProgramadaRef.current = emPausaProgramada; }, [emPausaProgramada]);

  // Função auxiliar para extrair operações pendentes do endpoint /ofs
  // O endpoint retorna OFs com lotes, cada lote com operacoes_operador
  // Precisamos achatar esta estrutura para ter uma lista de tarefas
  const extrairOperacoesPendentes = useCallback((ofsData, excludeOperacaoId = null) => {
    const operacoes = [];
    const todasOfs = [...(ofsData.novas || []), ...(ofsData.iniciadas || [])];
    
    for (const of of todasOfs) {
      for (const lote of (of.lotes || [])) {
        // Lotes ARR: filtrar pelas operações permitidas (não mostrar todas as ops da OF)
        const isArrLote = lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo';
        const permitidas = isArrLote ? (lote.operacoes_permitidas || []) : null;
        for (const op of (lote.operacoes_operador || [])) {
          if (isArrLote && permitidas && permitidas.length > 0 && !permitidas.includes(op.operacao_id)) continue;
          // Incluir apenas operações pendentes ou parciais (não completas nem em_curso)
          if (op.status === 'pendente' || op.status === 'parcial' || op.status === 'parcial_outros') {
            // Excluir a operação atual se especificado
            if (excludeOperacaoId && op.operacao_id === excludeOperacaoId) {
              continue;
            }
            operacoes.push({
              of_id: of.id,
              of_numero: of.numero_of,
              lote_id: lote.id,
              lote_codigo: lote.codigo_lote || lote.codigo || (lote.numero_lote != null ? `L${lote.numero_lote}` : 'Sem código'),
              // Usar quantidade_ajustada se existir (definida por contagem)
              // Anomalias NÃO reduzem a quantidade do lote — são rastreadas separadamente
              lote_quantidade: lote.quantidade_ajustada || lote.quantidade || 0,
              operacao_id: op.operacao_id,
              operacao_codigo: op.codigo,
              operacao_nome: op.nome,
              status: op.status,
              quantidade_produzida: op.quantidade_produzida,
              quantidade_total: op.quantidade_total
            });
          }
        }
      }
    }
    return operacoes;
  }, []);

  // Carregar user simulado se em modo de simulação
  useEffect(() => {
    const loadSimulatedUser = async () => {
      if (isSimulation && username && !user) {
        try {
          if (process.env.NODE_ENV === 'development') console.log('Loading simulated user:', username);
          const response = await axios.get(`${API}/operadores/username/${username}`);
          if (response.data) {
            setUser(response.data);
          }
        } catch (error) {
          console.error('Erro ao carregar user simulado:', error);
        }
      }
    };
    loadSimulatedUser();
  }, [isSimulation, username, user]);

  // Carregar produção ativa quando user estiver disponível
  useEffect(() => {
    if (user?.id) {
      // Se há parâmetro descongelar na URL, NÃO carregar producaoAtiva agora
      // — o useEffect de descongelar tratará disso após a API call
      const searchParams = new URLSearchParams(location.search);
      const descongelarId = searchParams.get('descongelar');
      if (!descongelarId) {
        loadProducaoAtiva();
      }
      loadTiposAnomalia();
      loadConfiguracao();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Processar parâmetro descongelar da URL (vindo do drawer de congeladas)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const descongelarId = searchParams.get('descongelar');
    
    if (descongelarId && user?.id) {
      // Evitar dupla execução
      if (descongelarProcessedRef.current === descongelarId) return;
      descongelarProcessedRef.current = descongelarId;
      
      // Verificar primeiro se já existe uma operação ativa
      const verificarEDescongelar = async () => {
        // Limpar o parâmetro da URL imediatamente para evitar múltiplas execuções
        const newUrl = location.pathname;
        window.history.replaceState({}, '', newUrl);
        
        try {
          // Tentar descongelar diretamente - o backend já verifica conflitos
          await axios.post(`${API}/producao/descongelar`, {
            registo_id: descongelarId,
            operador_id: user.id
          });
          
          toast.success('Operação retomada!');
          setVeioDeCongelada(true); // Marcar que veio de uma descongelação
          
          // Recarregar estado
          await loadProducaoAtiva();
          await loadOperacoesCongeladas();
          
        } catch (error) {
          console.error('Erro ao descongelar:', error);
          const errorMessage = error.response?.data?.detail || 'Erro ao retomar operação';
          const status = error.response?.status;
          
          if (status === 409 && errorMessage.includes('em pausa')) {
            toast.error('Está em pausa. Retome a operação pausada antes de retomar outra.');
          } else if (status === 400 && errorMessage.includes('já existe')) {
            setPendingDescongelarId(descongelarId);
            toast.error('Já existe uma operação em andamento. Congele-a primeiro.', { duration: 6000 });
          } else if (error.response?.status === 404 && errorMessage.includes('não encontrado')) {
            toast.info('Esta operação já foi retomada ou não existe mais.');
          } else {
            toast.error(errorMessage);
          }
          // SEMPRE carregar producaoAtiva após erro para sair do loading
          await loadProducaoAtiva();
          await loadOperacoesCongeladas();
        }
      };
      
      verificarEDescongelar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user?.id]);

  // Carregar configuração de horários e pausas
  const loadConfiguracao = async () => {
    try {
      const response = await axios.get(`${API}/configuracao`);
      if (response.data) {
        setConfiguracao(response.data);
        
        // Construir lista de pausas legais a partir da configuração
        const pausas = [];
        
        // Adicionar intervalo de almoço como pausa legal
        if (response.data.intervalo_almoco_inicio && response.data.intervalo_almoco_fim) {
          pausas.push({
            id: 'almoco',
            label: 'Almoço',
            icon: '🍽️',
            inicio: response.data.intervalo_almoco_inicio,
            fim: response.data.intervalo_almoco_fim,
            pausaLegal: true
          });
        }
        
        // Adicionar pausas adicionais configuradas
        if (response.data.pausas_adicionais) {
          response.data.pausas_adicionais.forEach((pausa, index) => {
            pausas.push({
              id: `pausa_adicional_${index}`,
              label: pausa.descricao || `Pausa ${index + 1}`,
              icon: '⏰',
              inicio: pausa.inicio,
              fim: pausa.fim,
              pausaLegal: true
            });
          });
        }
        
        setPausasLegais(pausas);
      }
    } catch (err) {
      console.error('Erro ao carregar configuração:', err);
    }
  };

  // Verificar se está na hora de uma pausa programada - AUTOMÁTICO
  useEffect(() => {
    // Se já está em pausa ou não há pausas configuradas, não verificar
    if (emPausa || !pausasLegais.length) return;

    const checkPausaProgramada = async () => {
      const agora = new Date();
      const horaAtual = `${agora.getHours().toString().padStart(2, '0')}:${agora.getMinutes().toString().padStart(2, '0')}`;

      for (const pausa of pausasLegais) {
        // IMPORTANTE: Ignorar almoço da automação - deve ser selecionado manualmente pelo operador
        if (pausa.id === 'almoco') continue;
        
        if (pausa.inicio && pausa.fim) {
          // Verificar se a hora atual está dentro do intervalo da pausa
          if (horaAtual >= pausa.inicio && horaAtual < pausa.fim) {
            // Se há produção ativa, pausar automaticamente
            if (producaoData?.registo?.id) {
              try {
                await axios.post(`${API}/producao/pausa`, {
                  registo_id: producaoData.registo.id,
                  motivo: pausa.label,
                  contagem_atual: null
                });
                
                setEmPausa(true);
                setEmPausaProgramada(true);
                setUltimoMotivoPausa(pausa.label);
                setPausaProgramadaAtual({
                  nome: pausa.label,
                  horaFim: pausa.fim,
                  icon: pausa.icon || '⏰'
                });
                await loadProducaoAtiva();
                toast.info(`⏰ Pausa automática: ${pausa.label}`, {
                  duration: 5000,
                  description: `Retoma às ${pausa.fim}`
                });
              } catch (error) {
                console.error('Erro ao iniciar pausa automática:', error);
              }
            } else {
              // Sem operação em curso - apenas notificar que é hora de pausa
              toast.info(`⏰ Hora de ${pausa.label}`, {
                duration: 5000,
                description: `Até às ${pausa.fim}`
              });
            }
            return; // Só processar uma pausa de cada vez
          }
        }
      }
    };

    // Verificar imediatamente
    checkPausaProgramada();

    // Verificar a cada minuto
    const interval = setInterval(checkPausaProgramada, 60000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producaoData, emPausa, pausasLegais]);

  // Auto-retomar pausas programadas quando o horário de fim chega
  useEffect(() => {
    if (!emPausaProgramada || !pausaProgramadaAtual?.horaFim || !producaoData?.registo?.id) return;

    const checkAutoResume = async () => {
      const agora = new Date();
      const horaAtual = `${agora.getHours().toString().padStart(2, '0')}:${agora.getMinutes().toString().padStart(2, '0')}`;

      if (horaAtual >= pausaProgramadaAtual.horaFim) {
        try {
          toast.info('Pausa terminada — a retomar automaticamente...', { duration: 3000 });
          await handleRetomar();
        } catch (error) {
          console.error('Erro ao auto-retomar pausa programada:', error);
        }
      }
    };

    // Verificar a cada 30 segundos
    const interval = setInterval(checkAutoResume, 30000);
    // Verificar imediatamente
    checkAutoResume();

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emPausaProgramada, pausaProgramadaAtual]);

  // Handler para iniciar pausa programada manualmente (caso necessário)
  const handleIniciarPausaProgramada = async () => {
    if (!pausaProgramadaAtual) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/producao/pausa`, {
        registo_id: producaoData.registo.id,
        motivo: pausaProgramadaAtual.nome,
        contagem_atual: null
      });
      
      setEmPausa(true);
      setEmPausaProgramada(true);
      setUltimoMotivoPausa(pausaProgramadaAtual.nome);
      setShowPausaProgramadaDialog(false);
      await loadProducaoAtiva();
      toast.success('Pausa iniciada');
    } catch (error) {
      toast.error('Erro ao iniciar pausa');
    } finally {
      setLoading(false);
    }
  };

  // Carregar tipos de anomalia do backend
  const loadTiposAnomalia = async () => {
    try {
      const response = await axios.get(`${API}/tipos-anomalia`);
      setTiposAnomaliaBackend(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar tipos de anomalia:', err);
    }
  };

  // Separar tipos por classificação
  const tiposDefeito = tiposAnomaliaBackend.filter(t => t.classificacao === 'defeito' || !t.classificacao);
  const tiposArranjo = tiposAnomaliaBackend.filter(t => t.classificacao === 'arranjo');

  // Combinar motivos de pausa base com pausas legais configuradas pelo gestor
  const MOTIVOS_PAUSA = useMemo(() => {
    const motivos = [...MOTIVOS_PAUSA_BASE];
    
    // Atualizar Almoço com horários da configuração
    const almocoIndex = motivos.findIndex(m => m.id === 'almoco');
    if (almocoIndex >= 0) {
      const almocoConfig = pausasLegais.find(p => p.id === 'almoco');
      if (almocoConfig) {
        motivos[almocoIndex] = {
          ...motivos[almocoIndex],
          inicio: almocoConfig.inicio,
          fim: almocoConfig.fim,
          horario: `${almocoConfig.inicio} - ${almocoConfig.fim}`
        };
      }
    }
    
    // NOTA: Pausas adicionais (manhã/tarde) são AUTOMÁTICAS
    // Por isso NÃO são adicionadas ao menu de seleção manual
    // O operador não precisa de as selecionar manualmente
    
    return motivos;
  }, [pausasLegais]);

  // Calcular tempo total de pausas completas
  const calcularTempoPausas = (pausas) => {
    if (!pausas || pausas.length === 0) return 0;
    return pausas.reduce((total, pausa) => {
      if (pausa.inicio && pausa.fim) {
        const inicio = new Date(pausa.inicio);
        const fim = new Date(pausa.fim);
        return total + Math.floor((fim - inicio) / 1000);
      }
      return total;
    }, 0);
  };

  useEffect(() => {
    if (!producaoData) {
      // Timer local (antes da API responder) usando timestamp do sessionStorage
      const inicioLocal = sessionStorage.getItem('producao_inicio_local');
      if (!inicioLocal) return;
      const localInterval = setInterval(() => {
        setTempoDecorrido(Math.max(0, Math.floor((Date.now() - new Date(inicioLocal).getTime()) / 1000)));
      }, 500);
      return () => clearInterval(localInterval);
    }
    // Guardar o início real da produção no sessionStorage (para restaurar ao reabrir)
    if (producaoData.registo?.inicio) {
      sessionStorage.setItem('producao_inicio_local', producaoData.registo.inicio);
    }

    // Timer rápido nos primeiros 3 segundos (200ms), depois normal (1000ms)
    let tickCount = 0;
    const getDelay = () => tickCount < 15 ? 200 : 1000;
    
    // Calcular tempo uma vez (para pausa ou estado inicial)
    const calcTempo = () => {
      const inicio = new Date(producaoData.registo.inicio);
      const agora = new Date();
      const diffTotal = Math.max(0, Math.floor((agora - inicio) / 1000));
      const tempoPausasCompletas = calcularTempoPausas(producaoData.registo.pausas);
      
      if (emPausa) {
        const pausaAtual = producaoData.registo.pausas?.find(p => p.inicio && !p.fim);
        if (pausaAtual) {
          const inicioPausa = new Date(pausaAtual.inicio);
          const tempoPausaAtualCalc = Math.max(0, Math.floor((agora - inicioPausa) / 1000));
          setTempoPausaAtual(tempoPausaAtualCalc);
          setTempoDecorrido(Math.max(0, diffTotal - tempoPausasCompletas - tempoPausaAtualCalc));
        } else {
          setTempoDecorrido(Math.max(0, diffTotal - tempoPausasCompletas));
        }
      } else {
        setTempoDecorrido(Math.max(0, diffTotal - tempoPausasCompletas));
        setTempoPausaAtual(0);
      }
    };
    
    // Calcular imediatamente
    calcTempo();
    
    // Se em pausa, NÃO continuar a tickar (tempo fica congelado)
    if (emPausa) return;
    
    let timeoutId;
    const tick = () => {
      tickCount++;
      calcTempo();
      
      // Calcular tempo extra (fora do horário normal)
      if (configuracao) {
        const horarioFim = configuracao.horario_fim || '17:00';
        const [hFim, mFim] = horarioFim.split(':').map(Number);
        
        const now = new Date();
        const fimHorarioNormal = new Date();
        fimHorarioNormal.setHours(hFim, mFim, 0, 0);
        
        if (now > fimHorarioNormal) {
          setForaHorario(true);
          setTempoExtra(Math.floor((now - fimHorarioNormal) / 1000));
        } else {
          setForaHorario(false);
          setTempoExtra(0);
        }
      }
      
      timeoutId = setTimeout(tick, getDelay());
    };

    // Tick inicial imediato
    tick();

    return () => clearTimeout(timeoutId);
  }, [producaoData, emPausa, configuracao]);

  // Polling + WebSocket: detetar quando o gestor encerra a operação remotamente
  useEffect(() => {
    if (!user?.id || !producaoData?.registo?.id) return;

    // Handler partilhado: verificar se a produção continua ativa e reagir caso não
    let cancelled = false;
    const handleEncerramentoRemoto = () => {
      if (operadorConcluiuRef.current) return;
      setProducaoData(null);
      setEmPausa(false);
      setTempoPausaAtual(0);
      setTempoDecorrido(0);
      sessionStorage.removeItem('producao_inicio_local');
      toast.info('A operação foi encerrada pelo gestor.');
      if (isSimulation) {
        safeNavigate(`/sim/operador/${username}`);
      } else {
        navigate('/operador');
      }
    };

    const checkAtiva = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/producao/ativa/${user.id}`);
        if (cancelled) return;
        if (!res.data || !res.data.registo) {
          // Produção já não está ativa
          handleEncerramentoRemoto();
        } else {
          const r = res.data.registo;
          // Detectar troca de operação pendente (gestor mudou enquanto operador estava activo)
          const tp = r?.troca_pendente;
          if (tp && !trocaPendente) {
            setTrocaPendente({ ...tp, registo_id: r.id });
            setProducaoData(res.data);
          }
          // Sincronizar estado de pausa AUTOMÁTICA imposta pelo backend (scheduler pausas legais).
          // O backend é a fonte da verdade — se o estado local divergir, alinhar.
          const ultimaPausa = (r.pausas && r.pausas.length) ? r.pausas[r.pausas.length - 1] : null;
          const pausaLegalAtiva = !!(r.em_pausa && ultimaPausa && ultimaPausa.auto === true && !ultimaPausa.fim);
          if (pausaLegalAtiva && !emPausaRef.current) {
            emPausaRef.current = true;
            emPausaProgramadaRef.current = true;
            setEmPausa(true);
            setEmPausaProgramada(true);
            setUltimoMotivoPausa(ultimaPausa.label || ultimaPausa.motivo || 'Pausa');
            toast.info(`Pausa automática: ${ultimaPausa.label || ultimaPausa.motivo || 'Pausa Programada'}`);
          } else if (!r.em_pausa && emPausaRef.current && emPausaProgramadaRef.current) {
            emPausaRef.current = false;
            emPausaProgramadaRef.current = false;
            setEmPausa(false);
            setEmPausaProgramada(false);
            setTempoPausaAtual(0);
            toast.success('Pausa terminada — produção retomada automaticamente.');
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.debug('Polling tick error:', err?.message || err);
      }
    };

    // Polling agressivo a cada 2s para deteção rápida da intervenção do gestor
    const pollInterval = setInterval(checkAtiva, 2000);

    // Re-fetch imediato quando o tab volta a estar visível ou ganha foco
    // (browsers throttle setInterval em tabs em segundo plano até ~60s)
    const onVisible = () => { if (document.visibilityState === 'visible') checkAtiva(); };
    const onFocus = () => checkAtiva();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    // WebSocket: escutar evento "operacao_encerrada" para reação instantânea
    const wsBase = BACKEND_URL || window.location.origin;
    const wsProtocol = wsBase.startsWith('https') ? 'wss' : 'ws';
    const wsUrl = `${wsProtocol}://${wsBase.replace(/^https?:\/\//, '')}/api/ws/notifications/${user.id}?perfil=operador`;
    let ws = null;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.tipo === 'operacao_encerrada' && !operadorConcluiuRef.current) {
            handleEncerramentoRemoto();
          }
        } catch (e) { if (process.env.NODE_ENV === 'development') console.warn('WS message error:', e); }
      };
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('WS init failed:', e);
    }

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      if (ws) {
        try { ws.close(); } catch (_e) { /* cleanup */ }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, producaoData?.registo?.id, isSimulation, username]);

  // Função para buscar quantidade disponível em tempo real
  // Útil quando múltiplos operadores trabalham no mesmo lote
  const fetchQuantidadeDisponivel = async () => {
    const { of, lote, operacao } = producaoData || {};
    if (!of?.id || !lote?.id || !operacao?.id) return null;
    
    setLoadingQuantidadeDisponivel(true);
    try {
      const response = await axios.get(`${API}/producao/quantidade-disponivel/${of.id}/${lote.id}/${operacao.id}`);
      setQuantidadeDisponivelRealTime(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar quantidade disponível:', error);
      return null;
    } finally {
      setLoadingQuantidadeDisponivel(false);
    }
  };

  const loadProducaoAtiva = async (retryCount = 0, skipNavigate = false) => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/producao/ativa/${user.id}`);
      if (!response.data) {
        // Se não encontrar e ainda há retries disponíveis, tentar novamente
        if (retryCount < 3) {
          if (process.env.NODE_ENV === 'development') console.log(`Produção não encontrada, tentativa ${retryCount + 1}/3...`);
          await new Promise(resolve => setTimeout(resolve, 100));
          return loadProducaoAtiva(retryCount + 1, skipNavigate);
        }
        // NÃO navegar se estamos a aguardar ação do utilizador após conclusão
        // ou se skipNavigate foi passado explicitamente
        if (skipNavigate || showSucessoDialog || showProximasOperacoes) {
          if (process.env.NODE_ENV === 'development') console.log('Produção concluída - aguardando ação do utilizador');
          return;
        }
        // Navegar de volta baseado no modo
        if (isSimulation) {
          safeNavigate(`/sim/operador/${username}`);
        } else {
          navigate('/operador');
        }
        return;
      }
      setProducaoData(response.data);
      
      // Calcular tempoDecorrido IMEDIATAMENTE (sem esperar o interval)
      if (response.data.registo?.inicio) {
        // Guardar início no sessionStorage para futuras montagens
        sessionStorage.setItem('producao_inicio_local', response.data.registo.inicio);
        
        const inicio = new Date(response.data.registo.inicio);
        const agora = new Date();
        const diffTotal = Math.max(0, Math.floor((agora - inicio) / 1000));
        const pausas = response.data.registo.pausas || [];
        const tempoPausasCompletas = pausas.reduce((total, pausa) => {
          if (pausa.inicio && pausa.fim) {
            return total + Math.floor((new Date(pausa.fim) - new Date(pausa.inicio)) / 1000);
          }
          return total;
        }, 0);
        const pausaAtual = pausas.find(p => p.inicio && !p.fim);
        const tempoPausaAtualCalc = pausaAtual 
          ? Math.max(0, Math.floor((agora - new Date(pausaAtual.inicio)) / 1000)) 
          : 0;
        const tempoReal = Math.max(0, diffTotal - tempoPausasCompletas - tempoPausaAtualCalc);
        setTempoDecorrido(tempoReal);
      }
      
      // Verificar se a produção veio de uma descongelação
      if (response.data.registo?.descongelado_em) {
        setVeioDeCongelada(true);
      }
      
      const pausas = response.data.registo.pausas || [];
      if (pausas.length > 0 && !pausas[pausas.length - 1].fim) {
        setEmPausa(true);
        // Guardar o último motivo de pausa
        const motivoAtual = pausas[pausas.length - 1].motivo || '';
        setUltimoMotivoPausa(motivoAtual);
        
        // Verificar se é uma pausa programada (legal) para mostrar hora de fim
        const pausaLegalEncontrada = pausasLegais.find(p => p.label === motivoAtual);
        if (pausaLegalEncontrada) {
          setEmPausaProgramada(true);
          setPausaProgramadaAtual({
            nome: pausaLegalEncontrada.label,
            horaFim: pausaLegalEncontrada.fim,
            icon: pausaLegalEncontrada.icon
          });
        }
        
        // Inicializar o tempo de pausa atual com o tempo já decorrido
        const pausaAtual = pausas[pausas.length - 1];
        if (pausaAtual.inicio) {
          const inicioPausa = new Date(pausaAtual.inicio);
          const agora = new Date();
          const tempoPausaCalc = Math.floor((agora - inicioPausa) / 1000);
          setTempoPausaAtual(tempoPausaCalc);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar produção:', error);
    }
  };

  const handlePausa = async () => {
    if (!motivoPausa) {
      toast.error('Selecione um motivo');
      return;
    }

    // Verificar se o motivo requer contagem obrigatória
    const motivoSelecionado = MOTIVOS_PAUSA.find(m => m.label === motivoPausa);
    if (motivoSelecionado?.requerContagem && !contagemPausa) {
      toast.error('A contagem de peças é obrigatória para este tipo de pausa');
      return;
    }

    // Atualizar UI IMEDIATAMENTE (optimistic update)
    setEmPausa(true);
    setTempoPausaAtual(0);
    setShowPausaDrawer(false);

    setLoading(true);
    try {
      await axios.post(`${API}/producao/pausa`, {
        registo_id: producaoData.registo.id,
        motivo: motivoPausa,
        quantidade_parcial: motivoSelecionado?.requerContagem ? parseInt(contagemPausa) : null
      });
      
      setUltimoMotivoPausa(motivoPausa);
      
      if (motivoSelecionado?.pausaLegal) {
        setEmPausaProgramada(true);
        setPausaProgramadaAtual({
          nome: motivoPausa,
          horaFim: motivoSelecionado.fim || null,
          icon: motivoSelecionado.icon
        });
      }
      
      setMotivoPausa('');
      setContagemPausa('');
      
      await loadProducaoAtiva();
      toast.success('Pausa registada');
    } catch (error) {
      // Reverter optimistic update em caso de erro
      setEmPausa(false);
      console.error('Erro ao registar pausa:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao registar pausa';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRetomar = async () => {
    if (!producaoData?.registo?.id) {
      toast.error('Sem registo ativo para retomar');
      return;
    }
    
    // Optimistic update IMEDIATO — o timer recomeça sem esperar pelo servidor
    setEmPausa(false);
    setEmPausaProgramada(false);
    setTempoPausaAtual(0);
    setUltimoMotivoPausa('');
    
    setLoading(true);
    try {
      // Pausas programadas (almoço e outras do horário) NÃO reiniciam o tempo
      // O tempo apenas continua de onde parou
      if (process.env.NODE_ENV === 'development') console.log('Retomando produção:', producaoData.registo.id);
      const response = await axios.post(`${API}/producao/retomar/${producaoData.registo.id}`, {
        novo_sprint: false // Nunca criar novo sprint - apenas continuar
      });
      if (process.env.NODE_ENV === 'development') console.log('Resposta retomar:', response.data);
      
      // Recarregar dados para obter a pausa com "fim" definido
      await loadProducaoAtiva();
    } catch (error) {
      // Reverter optimistic update em caso de erro
      setEmPausa(true);
      console.error('Erro ao retomar:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao retomar';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Função para terminar o dia de trabalho
  const handleTerminarDia = async () => {
    // Validar quantidade se tiver operação em aberto
    if (producaoData && !quantidadeProduzida) {
      toast.error('Por favor, insira a quantidade de peças produzidas');
      return;
    }
    
    setLoading(true);
    try {
      // Primeiro, concluir a operação atual como parcial se estiver em progresso
      if (producaoData?.registo?.id) {
        await axios.post(`${API}/producao/parar`, {
          registo_id: producaoData.registo.id,
          quantidade_produzida: parseInt(quantidadeProduzida) || 0,
          tipo_conclusao: 'parcial',
          motivo: 'Fim do dia de trabalho'
        });
      }
      
      // Registar fim do dia do operador
      await axios.post(`${API}/operadores/${user.id}/fim-dia`, {
        hora_fim: new Date().toISOString()
      });
      
      setShowTerminarDiaDialog(false);
      setQuantidadeProduzida('');
      
      // Navegar para a página de relatório do dia
      if (isSimulation) {
        safeNavigate(`/sim/operador/${username}/fim-dia`);
      } else {
        navigate('/operador/fim-dia');
      }
    } catch (error) {
      console.error('Erro ao terminar dia:', error);
      toast.error('Erro ao terminar o dia');
    } finally {
      setLoading(false);
    }
  };

  // Função para terminar o dia automaticamente pelo sistema
  // Verificar se está a trabalhar após o horário de saída
  // Em vez de mostrar alerta ao operador, apenas envia notificação silenciosa ao gestor
  useEffect(() => {
    if (!configuracao || !producaoData || horasExtrasConfirmado) return;

    const horarioFim = configuracao.horario_fim || '17:00';
    const [hFim, mFim] = horarioFim.split(':').map(Number);
    
    const checkHorasExtras = async () => {
      const agora = new Date();
      const horaSaidaHoje = new Date();
      horaSaidaHoje.setHours(hFim, mFim, 0, 0);
      
      // Adicionar 10 minutos ao horário de saída
      const horaSaidaMais10 = new Date(horaSaidaHoje.getTime() + 10 * 60 * 1000);
      
      // IMPORTANTE: Só enviar notificação se houver operação EM CURSO (registo ativo)
      const temOperacaoEmCurso = producaoData?.registo?.id && !emPausa;
      
      // Se passou o horário e tem operação em curso, enviar notificação silenciosa ao gestor
      // Apenas uma vez (usando horasExtrasConfirmado como flag)
      if (agora >= horaSaidaMais10 && temOperacaoEmCurso && !horasExtrasConfirmado) {
        // Marcar que já enviou a notificação para não enviar de novo
        setHorasExtrasConfirmado(true);
        
        // Enviar notificação silenciosa ao gestor
        try {
          await axios.post(`${API}/alertas-inatividade`, {
            operador_id: user?.id,
            operador_nome: user?.nome_completo || user?.nome || user?.username,
            of_id: producaoData.of?.id,
            of_numero: producaoData.of?.numero_of,
            lote_id: producaoData.lote?.id,
            lote_codigo: producaoData.lote?.codigo,
            operacao_id: producaoData.operacao?.id,
            operacao_nome: producaoData.operacao?.nome,
            motivo: 'operacao_aberta_apos_horario'
          });
          if (process.env.NODE_ENV === 'development') console.log('Notificação enviada ao gestor: operação aberta após horário');
        } catch (error) {
          if (process.env.NODE_ENV === 'development') console.log('Erro ao enviar notificação ao gestor:', error);
        }
      }
    };

    // Verificar a cada minuto
    checkHorasExtras();
    const interval = setInterval(checkHorasExtras, 60 * 1000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuracao, producaoData, horasExtrasConfirmado, emPausa]);

  // Carregar operações congeladas
  const loadOperacoesCongeladas = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/producao/congeladas/${user.id}`);
      setOperacoesCongeladas(response.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.log('Erro ao carregar operações congeladas:', error);
    }
    // Verifica também se há tarefa pontual congelada (constrange a 1 congelada total)
    try {
      const r = await axios.get(`${API}/tarefas/ativa/${user.id}`);
      if (r.data && (r.data.congelado || r.data.congelado_em)) {
        setTarefaPontualCongelada(r.data);
      } else {
        setTarefaPontualCongelada(null);
      }
    } catch (e) {
      setTarefaPontualCongelada(null);
    }
  }, [user?.id]);

  // Carregar operações congeladas ao montar
  useEffect(() => {
    loadOperacoesCongeladas();
  }, [loadOperacoesCongeladas]);

  // Função para chamar o gestor (envia alerta em tempo real)
  const handleChamarGestor = async () => {
    try {
      await axios.post(`${API}/alertas-gestor/chamar`, {
        operador_id: user?.id,
        operador_nome: user?.nome_completo || user?.nome || user?.username,
        of_id: producaoData?.of?.id,
        of_numero: producaoData?.of?.numero_of,
        lote_id: producaoData?.lote?.id || producaoData?.registo?.lote_id,
        lote_codigo: producaoData?.lote?.codigo_lote || producaoData?.registo?.lote_codigo,
        operacao_id: producaoData?.registo?.operacao_id,
        operacao_nome: operacao?.nome || producaoData?.operacao?.nome,
        operacao_codigo: operacao?.codigo || producaoData?.operacao?.codigo,
        marca: producaoData?.of?.cliente,
        modelo: producaoData?.of?.artigo,
      });
      toast.success('Gestor notificado! Alguém virá em breve.', { duration: 5000 });
    } catch (error) {
      console.error('Erro ao chamar gestor:', error);
      toast.error('Erro ao enviar pedido ao gestor');
    }
  };

  // Função para congelar operação atual
  const handleCongelar = async () => {
    if (!producaoData?.registo?.id) {
      toast.error('Nenhuma operação ativa');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/producao/congelar`, {
        operador_id: user.id,
        tempo_decorrido: tempoDecorrido
      });
      
      toast.success('Operação congelada! Pode iniciar outra tarefa.');
      setShowCongelarDialog(false);
      
      // Limpar estado local imediatamente para feedback visual rápido
      setProducaoData(null);
      sessionStorage.removeItem('producao_inicio_local');
      
      // Redirecionar para home do operador para escolher nova tarefa
      if (isSimulation) {
        safeNavigate(`/sim/operador/${username}`);
      } else {
        navigate('/operador');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao congelar operação');
      setLoading(false);
    }
  };

  // Função para descongelar operação
  const handleDescongelar = async (registoId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/producao/descongelar`, {
        registo_id: registoId,
        operador_id: user.id
      });
      
      toast.success('Operação retomada!');
      setShowOperacoesCongeladas(false);
      
      // Recarregar estado
      await loadProducaoAtiva();
      await loadOperacoesCongeladas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao descongelar operação');
    } finally {
      setLoading(false);
    }
  };

  // Função para concluir operação congelada
  const handleConcluirCongelada = async (registoId, tipoConclusao) => {
    setLoading(true);
    try {
      await axios.post(`${API}/producao/congelada/concluir`, {
        registo_id: registoId,
        quantidade_final: parseInt(quantidadeProduzida) || 0,
        tipo_conclusao: tipoConclusao
      });
      
      toast.success('Operação concluída!');
      setQuantidadeProduzida('');
      await loadOperacoesCongeladas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir operação');
    } finally {
      setLoading(false);
    }
  };

  // Verificar se precisa de confirmação antes de concluir
  const handleTentarConcluir = () => {
    // Permitir 0 peças (cenário válido: operador iniciou mas não produziu)
    if (quantidadeProduzida === '' || quantidadeProduzida === null || quantidadeProduzida === undefined) {
      toast.error('Indique a quantidade');
      return;
    }
    if (parseInt(quantidadeProduzida) < 0) {
      toast.error('A quantidade não pode ser negativa');
      return;
    }
    
    const lote = producaoData?.lote;
    const quantidadeTotal = lote?.quantidade_ajustada || lote?.quantidade || 0;
    const quantidadeInserida = parseInt(quantidadeProduzida);
    const quantidadeAnterior = quantidadeJaProduzida || 0;
    const quantidadeTotalProduzida = quantidadeInserida + quantidadeAnterior;
    
    // Validar que o TOTAL (anterior + nova) não excede o lote
    if (quantidadeTotal > 0 && quantidadeTotalProduzida > quantidadeTotal) {
      const maximo = Math.max(0, quantidadeTotal - quantidadeAnterior);
      toast.error(`Quantidade máxima permitida: ${maximo} peças (${quantidadeAnterior} já produzidas anteriormente)`);
      return;
    }
    
    // Se o TOTAL for inferior ao lote, pedir confirmação de parcial
    if (quantidadeTotal > 0 && quantidadeTotalProduzida < quantidadeTotal) {
      setShowConfirmacaoParcial(true);
    } else {
      handleConcluir();
    }
  };

  const handleConcluir = async () => {
    // Verificar se há um registo de produção válido
    if (!producaoData?.registo?.id) {
      toast.error('Não há produção ativa para concluir');
      setShowConcluirDrawer(false);
      return;
    }
    
    // Permitir 0 peças (cenário válido: operador iniciou mas não produziu)
    if (quantidadeProduzida === '' || quantidadeProduzida === null || quantidadeProduzida === undefined) {
      toast.error('Indique a quantidade');
      return;
    }
    if (parseInt(quantidadeProduzida) < 0) {
      toast.error('A quantidade não pode ser negativa');
      return;
    }
    
    // Validação extra no backend também impede exceder o limite
    const lote = producaoData?.lote;
    const operacao = producaoData?.operacao; // Incluir operação para verificar is_contagem
    const quantidadeTotal = lote?.quantidade_ajustada || lote?.quantidade || 0;
    const quantidadeInserida = parseInt(quantidadeProduzida);
    const quantidadeAnterior = quantidadeJaProduzida || 0;
    const quantidadeTotalProduzida = quantidadeInserida + quantidadeAnterior;
    
    if (quantidadeTotal > 0 && quantidadeTotalProduzida > quantidadeTotal) {
      const maximo = Math.max(0, quantidadeTotal - quantidadeAnterior);
      toast.error(`Quantidade máxima permitida: ${maximo} peças (${quantidadeAnterior} já produzidas)`);
      return;
    }
    
    setShowConfirmacaoParcial(false); // Fechar dialog de confirmação se estava aberto

    setLoading(true);
    try {
      // Guardar dados da OF/Lote ANTES de concluir (para uso posterior no handleFecharSucesso)
      const dadosParaGuardar = {
        of_id: producaoData.of?.id,
        of_numero: producaoData.of?.numero_of,
        lote_id: producaoData.lote?.id || producaoData.registo?.lote_id,
        operacao_id: producaoData.registo?.operacao_id
      };
      setDadosConclusaoAnterior(dadosParaGuardar);
      setOfConcluida(producaoData.of);
      
      // Se houver anomalias, registá-las primeiro
      if (anomaliasProducao.length > 0) {
        for (const anomalia of anomaliasProducao) {
          await axios.post(`${API}/anomalias-producao`, {
            of_id: producaoData.of.id,
            lote_id: producaoData.lote?.id || producaoData.registo.lote_id,
            operacao_id: producaoData.registo.operacao_id,
            operador_id: user.id,
            tipo: anomalia.classificacao || 'defeito', // 'defeito' ou 'arranjo'
            tipo_anomalia_id: anomalia.tipo_anomalia_id,
            tipo_nome: anomalia.tipo_nome,
            quantidade: parseInt(anomalia.quantidade),
            observacoes: anomalia.observacoes,
            fotos: anomalia.fotos
          });
        }
      }
      
      await axios.post(`${API}/producao/parar`, {
        registo_id: producaoData.registo.id,
        quantidade_produzida: parseInt(quantidadeProduzida),
        tipo_conclusao: 'concluido'
      });
      
      // Se é operação de contagem, registar a contagem e ajustar quantidades
      const isOperacaoContagem = operacao?.is_contagem || producaoData?.operacao?.is_contagem;
      if (process.env.NODE_ENV === 'development') console.log('[CONTAGEM] Verificando is_contagem:', { 
        operacao_is_contagem: operacao?.is_contagem,
        producaoData_operacao_is_contagem: producaoData?.operacao?.is_contagem,
        resultado: isOperacaoContagem
      });
      
      if (isOperacaoContagem) {
        const quantidadeEsperada = lote?.quantidade_ajustada || lote?.quantidade || 0;
        const quantidadeContada = parseInt(quantidadeProduzida);
        
        if (process.env.NODE_ENV === 'development') console.log('[CONTAGEM] Enviando para API:', {
          of_id: producaoData.of?.id,
          lote_id: producaoData.lote?.id || producaoData.registo.lote_id,
          operacao_id: producaoData.registo.operacao_id,
          quantidade_contada: quantidadeContada,
          quantidade_esperada: quantidadeEsperada
        });
        
        try {
          const contagemRes = await axios.post(`${API}/producao/contagem`, {
            of_id: producaoData.of?.id,
            lote_id: producaoData.lote?.id || producaoData.registo.lote_id,
            operacao_id: producaoData.registo.operacao_id,
            operador_id: user.id,
            quantidade_contada: quantidadeContada,
            quantidade_esperada: quantidadeEsperada
          });
          
          const resultado = contagemRes.data;
          if (process.env.NODE_ENV === 'development') console.log('[CONTAGEM] Resposta da API:', resultado);
          if (resultado.diferenca !== 0) {
            const tipoMsg = resultado.tipo === 'negativa' ? '⚠️ Falta' : '⚠️ Excesso';
            const corToast = resultado.tipo === 'negativa' ? 'warning' : 'info';
            toast[corToast](`${tipoMsg} detectada: ${Math.abs(resultado.diferenca)} peças`, { duration: 5000 });
          }
        } catch (contagemError) {
          console.error('[CONTAGEM] Erro ao registar contagem:', contagemError);
          toast.error('Erro ao registar contagem: ' + (contagemError.response?.data?.detail || contagemError.message));
        }
      }
      
      // Verificar se há mais operações na mesma OF para este operador
      try {
        const ofsRes = await axios.get(`${API}/operadores/${user.id}/ofs`);
        const todasOperacoes = extrairOperacoesPendentes(ofsRes.data, producaoData.registo.operacao_id);
        
        // Filtrar operações do mesmo LOTE que acabou de concluir (prioridade)
        // Se não houver, mostrar outras operações da mesma OF
        const loteIdAtual = producaoData.lote?.id || producaoData.registo.lote_id;
        const ofIdAtual = producaoData.of?.id;
        
        const operacoesMesmoLote = todasOperacoes.filter(t => 
          t.lote_id === loteIdAtual
        );
        
        const operacoesMesmaOf = todasOperacoes.filter(t => 
          t.of_id === ofIdAtual && 
          t.lote_id !== loteIdAtual // Excluir as do mesmo lote já listadas
        );
        
        // Priorizar operações do mesmo lote, depois outras da mesma OF
        const todasProximas = [...operacoesMesmoLote, ...operacoesMesmaOf];
        
        if (process.env.NODE_ENV === 'development') console.log('Próximas operações encontradas:', {
          mesmoLote: operacoesMesmoLote.length,
          mesmaOf: operacoesMesmaOf.length,
          total: todasProximas.length
        });
        
        if (todasProximas.length > 0) {
          // Há mais operações na mesma OF
          setProximasOperacoes(todasProximas);
          setOfConcluida(producaoData.of);
          setShowConcluirDrawer(false);
          setShowProximasOperacoes(true);
        } else {
          // Não há mais operações, mostrar diálogo de sucesso normal
          await carregarFraseFimTarefa();
          setShowConcluirDrawer(false);
          setShowSucessoDialog(true);
        }
      } catch (error) {
        console.error('Erro ao buscar próximas operações:', error);
        // Em caso de erro, mostrar diálogo normal
        await carregarFraseFimTarefa();
        setShowConcluirDrawer(false);
        setShowSucessoDialog(true);
      }
      
    } catch (error) {
      toast.error('Erro ao concluir');
    } finally {
      setLoading(false);
    }
  };
  
  // Função para fechar diálogo de sucesso e verificar próximas operações
  const handleFecharSucesso = async () => {
    setShowSucessoDialog(false);
    
    // Usar dados guardados da conclusão anterior (prioridade máxima)
    const ofIdAnterior = dadosConclusaoAnterior?.of_id;
    const loteIdAnterior = dadosConclusaoAnterior?.lote_id;
    const operacaoIdAnterior = dadosConclusaoAnterior?.operacao_id;
    
    if (process.env.NODE_ENV === 'development') console.log('handleFecharSucesso chamado:', { 
      ofIdAnterior, 
      loteIdAnterior, 
      operacaoIdAnterior,
      dadosConclusaoAnterior 
    });
    
    // Se não temos dados guardados, ir para home
    if (!ofIdAnterior) {
      if (process.env.NODE_ENV === 'development') console.log('Sem dados guardados, indo para home');
      setDadosConclusaoAnterior(null);
      if (isSimulation) {
        navigate(`/sim/operador/${username}`);
      } else {
        navigate('/operador');
      }
      return;
    }
    
    // Verificar se há mais operações na mesma OF/lote
    try {
      const ofsRes = await axios.get(`${API}/operadores/${user?.id}/ofs`);
      const todasOperacoes = extrairOperacoesPendentes(ofsRes.data, operacaoIdAnterior);
      
      if (process.env.NODE_ENV === 'development') console.log('Operações pendentes encontradas:', todasOperacoes.length);
      
      if (todasOperacoes.length > 0) {
        // Prioridade 1: Operações do mesmo LOTE
        const operacoesMesmoLote = todasOperacoes.filter(t => 
          t.lote_id === loteIdAnterior
        );
        
        // Prioridade 2: Outros lotes da mesma OF
        const operacoesMesmaOf = todasOperacoes.filter(t => 
          t.of_id === ofIdAnterior && t.lote_id !== loteIdAnterior
        );
        
        const todasProximas = [...operacoesMesmoLote, ...operacoesMesmaOf];
        
        if (process.env.NODE_ENV === 'development') console.log('Próximas operações:', {
          mesmoLote: operacoesMesmoLote.length,
          mesmaOf: operacoesMesmaOf.length,
          total: todasProximas.length
        });
        
        if (todasProximas.length > 0) {
          setProximasOperacoes(todasProximas);
          setShowProximasOperacoes(true);
          return;
        }
      }
    } catch (error) {
      console.error('Erro ao verificar próximas operações:', error);
    }
    
    // Limpar dados guardados
    setDadosConclusaoAnterior(null);
    
    // Se não houver mais operações, navegar para home
    if (process.env.NODE_ENV === 'development') console.log('Sem mais operações, indo para home');
    if (isSimulation) {
      navigate(`/sim/operador/${username}`);
    } else {
      navigate('/operador');
    }
  };

  // Função para concluir com anomalias (simplificado)
  const handleConcluirComAnomalias = async () => {
    if (!quantidadeProduzida || parseInt(quantidadeProduzida) < 0) {
      toast.error('Indique a quantidade de peças boas');
      return;
    }
    
    setLoading(true);
    try {
      // Guardar dados da OF/Lote ANTES de concluir (para uso posterior no handleFecharSucesso)
      const dadosParaGuardar = {
        of_id: producaoData.of?.id,
        of_numero: producaoData.of?.numero_of,
        lote_id: producaoData.lote?.id || producaoData.registo?.lote_id,
        operacao_id: producaoData.registo?.operacao_id
      };
      setDadosConclusaoAnterior(dadosParaGuardar);
      setOfConcluida(producaoData.of);
      
      // Se houver anomalias, criar registo de anomalias pendentes de classificação
      if (quantidadeAnomalias && quantidadeAnomalias > 0) {
        await axios.post(`${API}/anomalias-producao`, {
          of_id: producaoData.of.id,
          lote_id: producaoData.lote?.id || producaoData.registo.lote_id,
          operacao_id: producaoData.registo.operacao_id,
          operador_id: user.id,
          tipo: 'pendente_classificacao',
          tipo_anomalia_id: null,
          tipo_nome: 'Pendente de Classificação',
          quantidade: parseInt(quantidadeAnomalias),
          observacoes: 'Anomalia registada pelo operador - aguarda classificação pelo gestor'
        });
      }
      
      // Concluir a produção com a quantidade de peças boas
      await axios.post(`${API}/producao/parar`, {
        registo_id: producaoData.registo.id,
        quantidade_produzida: parseInt(quantidadeProduzida),
        tipo_conclusao: 'concluido'
      });
      
      // Verificar se há mais operações na mesma OF para este operador
      try {
        const ofsRes = await axios.get(`${API}/operadores/${user.id}/ofs`);
        const todasOperacoes = extrairOperacoesPendentes(ofsRes.data, producaoData.registo.operacao_id);
        
        // Filtrar operações do mesmo LOTE que acabou de concluir (prioridade)
        const loteIdAtual = producaoData.lote?.id || producaoData.registo.lote_id;
        const ofIdAtual = producaoData.of?.id;
        
        const operacoesMesmoLote = todasOperacoes.filter(t => 
          t.lote_id === loteIdAtual
        );
        
        const operacoesMesmaOf = todasOperacoes.filter(t => 
          t.of_id === ofIdAtual && 
          t.lote_id !== loteIdAtual
        );
        
        const todasProximas = [...operacoesMesmoLote, ...operacoesMesmaOf];
        
        if (todasProximas.length > 0) {
          setProximasOperacoes(todasProximas);
          setOfConcluida(producaoData.of);
          setShowConcluirDrawer(false);
          setConcluirComAnomalias(false);
          setQuantidadeAnomalias(0);
          setShowProximasOperacoes(true);
        } else {
          await carregarFraseFimTarefa();
          setShowConcluirDrawer(false);
          setConcluirComAnomalias(false);
          setQuantidadeAnomalias(0);
          setShowSucessoDialog(true);
        }
      } catch (error) {
        await carregarFraseFimTarefa();
        setShowConcluirDrawer(false);
        setConcluirComAnomalias(false);
        setQuantidadeAnomalias(0);
        setShowSucessoDialog(true);
      }
      
    } catch (error) {
      console.error('Erro ao concluir com anomalias:', error);
      toast.error('Erro ao concluir');
    } finally {
      setLoading(false);
    }
  };

  // Função para concluir PARCIAL (operação fica ABERTA)
  const handleConcluirParcial = async (qtdProduzida, qtdAnomalias) => {
    operadorConcluiuRef.current = true; // Sinalizar que foi o operador a concluir
    setLoading(true);
    try {
      // Se houver anomalias, criar registo
      if (qtdAnomalias > 0) {
        await axios.post(`${API}/anomalias-producao`, {
          of_id: producaoData.of.id,
          lote_id: producaoData.lote?.id || producaoData.registo.lote_id,
          operacao_id: producaoData.registo.operacao_id,
          operador_id: user.id,
          tipo: 'pendente_classificacao',
          tipo_anomalia_id: null,
          tipo_nome: 'Pendente de Classificação',
          quantidade: qtdAnomalias,
          observacoes: 'Anomalia registada pelo operador (produção parcial) - aguarda classificação pelo gestor'
        });
        // Criar alerta ao gestor sobre anomalias
        try {
          await axios.post(`${API}/alertas-anomalias`, {
            operador_id: user?.id,
            operador_nome: user?.nome_completo || user?.username,
            of_id: producaoData.of?.id || '',
            of_numero: producaoData.of?.numero_of || '',
            lote_id: producaoData.lote?.id || '',
            lote_codigo: producaoData.lote?.codigo_lote || '',
            operacao_id: producaoData.registo?.operacao_id || '',
            operacao_nome: `${operacaoAtual?.codigo || ''} ${operacaoAtual?.nome || ''}`.trim(),
            quantidade_anomalias: qtdAnomalias,
            descricao: `Operador ${user?.nome_completo || user?.username} registou ${qtdAnomalias} anomalia${qtdAnomalias !== 1 ? 's' : ''} na operação ${operacaoAtual?.codigo} ${operacaoAtual?.nome} (parcial)`
          });
        } catch (notifError) {
          if (process.env.NODE_ENV === 'development') console.log('Erro ao criar alerta de anomalias:', notifError);
        }
      }
      
      // Registar produção parcial — PARA a produção (fim do sprint)
      // A operação fica em estado "parcial" (não concluída, não pausada)
      // O operador ou outro pode iniciar um novo sprint mais tarde
      await axios.post(`${API}/producao/parar`, {
        registo_id: producaoData.registo.id,
        quantidade_produzida: qtdProduzida,
        tipo_conclusao: 'parcial',
        anomalias_count: qtdAnomalias,
        desvio: 0,
        motivo_parcial: 'Produção parcial'
      });
      
      toast.success(`Produção parcial registada: ${qtdProduzida} peças boas${qtdAnomalias > 0 ? ` + ${qtdAnomalias} anomalias` : ''}`);
      
      // Limpar estado — produção terminada (sprint encerrado)
      setShowConcluirDrawer(false);
      setModoConclusao('');
      setQuantidadeAnomalias(0);
      setProducaoData(null);
      
      // Navegar para Home — o operador encerrou este sprint
      if (isSimulation) {
        navigate(`/sim/operador/${username}`);
      } else {
        navigate('/operador');
      }
      
    } catch (error) {
      console.error('Erro na produção parcial:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registar produção parcial');
    } finally {
      setLoading(false);
    }
  };

  // Função para SAIR SEM CONTAR (cancela a produção sem afetar quantidades do lote)
  const handleSairSemContar = async () => {
    if (!producaoData?.registo?.id) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/producao/${producaoData.registo.id}`);
      toast.success('Saiu da operação sem contar');
      setProducaoData(null);
      setContagemAtualContagem(0);
      setQuantidadeProduzida('');
      if (isSimulation) {
        navigate(`/sim/operador/${username}`);
      } else {
        navigate('/operador');
      }
    } catch (error) {
      console.error('Erro ao sair sem contar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao sair da operação');
    } finally {
      setLoading(false);
    }
  };

  // Função para CONCLUIR operação (operação fica FECHADA)
  const handleConcluirCompleto = async (qtdProduzida, qtdAnomalias, qtdDesvio) => {
    operadorConcluiuRef.current = true; // Sinalizar que foi o operador a concluir
    setLoading(true);
    try {
      // Guardar dados da OF/Lote ANTES de concluir
      const dadosParaGuardar = {
        of_id: producaoData.of?.id,
        of_numero: producaoData.of?.numero_of,
        lote_id: producaoData.lote?.id || producaoData.registo?.lote_id,
        operacao_id: producaoData.registo?.operacao_id
      };
      setDadosConclusaoAnterior(dadosParaGuardar);
      setOfConcluida(producaoData.of);
      
      // Se houver anomalias, criar registo
      if (qtdAnomalias > 0) {
        await axios.post(`${API}/anomalias-producao`, {
          of_id: producaoData.of.id,
          lote_id: producaoData.lote?.id || producaoData.registo.lote_id,
          operacao_id: producaoData.registo.operacao_id,
          operador_id: user.id,
          tipo: 'pendente_classificacao',
          tipo_anomalia_id: null,
          tipo_nome: 'Pendente de Classificação',
          quantidade: qtdAnomalias,
          observacoes: 'Anomalia registada pelo operador - aguarda classificação pelo gestor'
        });
      }
      
      // Se houver desvio, criar notificação ao gestor
      if (qtdDesvio > 0) {
        try {
          await axios.post(`${API}/notificacoes`, {
            tipo: 'alerta_desvio',
            titulo: 'Desvio de Quantidade',
            mensagem: `Operador ${user?.nome_completo || user?.username} registou desvio de ${qtdDesvio} peças na operação ${operacao?.codigo || operacaoAtual?.codigo} ${operacao?.nome || operacaoAtual?.nome}`,
            dados: {
              operador_id: user?.id,
              of_id: producaoData.of?.id,
              lote_id: producaoData.lote?.id,
              operacao_id: producaoData.registo?.operacao_id,
              desvio: qtdDesvio
            }
          });
        } catch (notifError) {
          if (process.env.NODE_ENV === 'development') console.log('Erro ao criar notificação de desvio:', notifError);
        }
      }
      
      // Se houver anomalias, criar alerta ao gestor
      if (qtdAnomalias > 0) {
        try {
          await axios.post(`${API}/alertas-anomalias`, {
            operador_id: user?.id,
            operador_nome: user?.nome_completo || user?.username,
            of_id: producaoData.of?.id || '',
            of_numero: producaoData.of?.numero_of || '',
            lote_id: producaoData.lote?.id || '',
            lote_codigo: producaoData.lote?.codigo_lote || '',
            operacao_id: producaoData.registo?.operacao_id || '',
            operacao_nome: `${operacao?.codigo || operacaoAtual?.codigo || ''} ${operacao?.nome || operacaoAtual?.nome || ''}`.trim(),
            quantidade_anomalias: qtdAnomalias,
            descricao: `Operador ${user?.nome_completo || user?.username} registou ${qtdAnomalias} anomalia${qtdAnomalias !== 1 ? 's' : ''} na operação ${operacao?.codigo || operacaoAtual?.codigo} ${operacao?.nome || operacaoAtual?.nome}`
          });
        } catch (notifError) {
          if (process.env.NODE_ENV === 'development') console.log('Erro ao criar alerta de anomalias:', notifError);
        }
      }
      
      // Concluir a produção - a operação fica CONCLUÍDA
      await axios.post(`${API}/producao/parar`, {
        registo_id: producaoData.registo.id,
        quantidade_produzida: qtdProduzida,
        tipo_conclusao: 'concluido',
        desvio: qtdDesvio || 0,
        anomalias_count: qtdAnomalias || 0
      });
      
      // Verificar se há mais operações na mesma OF para este operador
      try {
        const ofsRes = await axios.get(`${API}/operadores/${user.id}/ofs`);
        const todasOperacoes = extrairOperacoesPendentes(ofsRes.data, producaoData.registo.operacao_id);
        
        // Filtrar operações do mesmo LOTE que acabou de concluir (prioridade)
        const loteIdAtual = producaoData.lote?.id || producaoData.registo.lote_id;
        const ofIdAtual = producaoData.of?.id;
        
        const operacoesMesmoLote = todasOperacoes.filter(t => 
          t.lote_id === loteIdAtual
        );
        
        const operacoesMesmaOf = todasOperacoes.filter(t => 
          t.of_id === ofIdAtual && 
          t.lote_id !== loteIdAtual
        );
        
        const todasProximas = [...operacoesMesmoLote, ...operacoesMesmaOf];
        
        if (todasProximas.length > 0) {
          setProximasOperacoes(todasProximas);
          setShowConcluirDrawer(false);
          setModoConclusao('');
          setQuantidadeAnomalias(0);
          setQuantidadeDesvio(0);
          setShowProximasOperacoes(true);
          setProducaoData(null); // Limpar DEPOIS de definir o dialog (evita flash "A carregar...")
          sessionStorage.removeItem('producao_inicio_local');
        } else {
          await carregarFraseFimTarefa();
          setShowConcluirDrawer(false);
          setModoConclusao('');
          setQuantidadeAnomalias(0);
          setQuantidadeDesvio(0);
          setShowSucessoDialog(true);
          setProducaoData(null); // Limpar DEPOIS de definir o dialog
          sessionStorage.removeItem('producao_inicio_local');
        }
      } catch (error) {
        await carregarFraseFimTarefa();
        setShowConcluirDrawer(false);
        setModoConclusao('');
        setQuantidadeAnomalias(0);
        setQuantidadeDesvio(0);
        setShowSucessoDialog(true);
        setProducaoData(null); // Limpar DEPOIS de definir o dialog
        sessionStorage.removeItem('producao_inicio_local');
      }
      
    } catch (error) {
      console.error('Erro ao concluir operação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao concluir');
    } finally {
      setLoading(false);
    }
  };

  // Adicionar anomalia à lista
  const handleAdicionarAnomalia = () => {
    if (!novaAnomalia.tipo_anomalia_id || !novaAnomalia.quantidade || parseInt(novaAnomalia.quantidade) <= 0) {
      toast.error('Preencha o tipo e a quantidade da anomalia');
      return;
    }
    
    // Encontrar o tipo selecionado para guardar os detalhes
    const tipoSelecionado = tiposAnomaliaBackend.find(t => t.id === novaAnomalia.tipo_anomalia_id);
    
    setAnomaliasProducao(prev => [...prev, { 
      ...novaAnomalia, 
      id: Date.now(),
      tipo_nome: tipoSelecionado?.nome || '',
      tipo_codigo: tipoSelecionado?.codigo || '',
      tipo_cor: tipoSelecionado?.cor || '#ef4444',
      classificacao: tipoSelecionado?.classificacao || 'defeito'
    }]);
    setNovaAnomalia({ tipo_anomalia_id: '', classificacao: '', quantidade: '', observacoes: '', fotos: [] });
    toast.success('Anomalia adicionada');
  };

  // Upload de foto para anomalia
  const handleUploadFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Verificar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A foto é muito grande. Máximo: 5MB');
      return;
    }
    
    setUploadingFoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/anomalias/upload-foto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Adicionar URL da foto à lista
      setNovaAnomalia(prev => ({
        ...prev,
        fotos: [...prev.fotos, response.data.foto_url]
      }));
      toast.success('Foto adicionada');
    } catch (error) {
      console.error('Erro ao carregar foto:', error);
      toast.error('Erro ao carregar foto');
    } finally {
      setUploadingFoto(false);
      // Limpar input para permitir nova selecção
      if (fotoInputRef.current) {
        fotoInputRef.current.value = '';
      }
    }
  };

  // Remover foto da lista
  const handleRemoverFoto = (index) => {
    setNovaAnomalia(prev => ({
      ...prev,
      fotos: prev.fotos.filter((_, i) => i !== index)
    }));
  };

  // Remover anomalia da lista
  const handleRemoverAnomalia = (id) => {
    setAnomaliasProducao(prev => prev.filter(a => a.id !== id));
  };

  // Calcular total de anomalias
  const totalAnomalias = anomaliasProducao.reduce((acc, a) => acc + parseInt(a.quantidade || 0), 0);

  const handleParcial = async () => {
    // Permitir 0 peças (cenário válido: operador iniciou mas não produziu)
    if (quantidadeProduzida === '' || quantidadeProduzida === null || quantidadeProduzida === undefined) {
      toast.error('Indique a quantidade');
      return;
    }
    if (parseInt(quantidadeProduzida) < 0) {
      toast.error('A quantidade não pode ser negativa');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/producao/parar`, {
        registo_id: producaoData.registo.id,
        quantidade_produzida: parseInt(quantidadeProduzida),
        tipo_conclusao: 'parcial',
        motivo_parcial: motivoParcial
      });
      
      toast.success('Produção parcial registada');
      if (isSimulation) {
        navigate(`/sim/operador/${username}`);
      } else {
        navigate('/operador');
      }
    } catch (error) {
      toast.error('Erro ao registar');
    } finally {
      setLoading(false);
    }
  };

  const handleReportarOcorrencia = async () => {
    if (!tipoOcorrencia) {
      toast.error('Selecione o tipo de ocorrência');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/ocorrencias`, {
        of_id: producaoData.of.id,
        operador_id: user.id,
        tipo: tipoOcorrencia,
        descricao: descricaoOcorrencia,
        quantidade_afetada: quantidadeAfetada ? parseInt(quantidadeAfetada) : null,
        status: 'aberta'
      });
      
      toast.success('Obrigada por reportar!');
      setShowOcorrenciaDrawer(false);
      setTipoOcorrencia('');
      setDescricaoOcorrencia('');
      setQuantidadeAfetada('');
    } catch (error) {
      toast.error('Erro ao reportar');
    } finally {
      setLoading(false);
    }
  };

  const handlePedirRevisao = async () => {
    setLoading(true);
    try {
      // Por agora, só registamos na produção (futuro: criar endpoint específico)
      toast.success('Pedido de revisão enviado ao gestor');
      setShowRevisaoDrawer(false);
      setMotivoRevisao('');
    } catch (error) {
      toast.error('Erro ao enviar pedido');
    } finally {
      setLoading(false);
    }
  };

  const formatTempo = (segundos) => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:${String(segs).padStart(2, '0')}`;
  };

  // Handler para aceitar troca de operação efectuada pelo gestor
  const handleAceitarTroca = async () => {
    if (!trocaPendente?.registo_id) return;
    setSubmittingTroca(true);
    try {
      await axios.post(`${API}/registos-tempo/${trocaPendente.registo_id}/aceitar-troca`);
      setTrocaPendente(null);
      // Refrescar producaoData para garantir UI sincronizada
      try {
        const res = await axios.get(`${API}/producao/ativa/${user.id}`);
        if (res.data) setProducaoData(res.data);
      } catch (e) { if (process.env.NODE_ENV === 'development') console.debug('Refresh produção falhou:', e?.message || e); }
      toast.success('Operação atualizada');
    } catch (e) {
      toast.error('Erro ao confirmar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmittingTroca(false);
    }
  };

  // Overlay global de troca pendente — renderizado via Portal, aparece sobre qualquer ecrã
  const trocaOverlay = (
    <TrocaPendenteOverlay troca={trocaPendente} onAceitar={handleAceitarTroca} submitting={submittingTroca} />
  );

  if (!producaoData) {
    // Se há diálogos de sucesso ou próximas operações para mostrar, renderizá-los
    if (showSucessoDialog || showProximasOperacoes) {
      return (
        <>
        {trocaOverlay}
        <div className="min-h-screen bg-warm-bg">
          {/* Dialog de Sucesso */}
          <Dialog open={showSucessoDialog} onOpenChange={(open) => { if (!open) handleFecharSucesso(); }}>
            <DialogContent className="max-w-md mx-auto rounded-3xl border-0 shadow-2xl bg-gradient-to-b from-green-50 to-white p-8">
              <div className="text-center space-y-6">
                <div className="relative mx-auto w-24 h-24">
                  <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-25"></div>
                  <div className="relative flex items-center justify-center w-24 h-24 bg-green-500 rounded-full">
                    <CheckCircle2 className="h-12 w-12 text-white" />
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-green-700 mb-2">Tarefa Concluida!</h2>
                  <p className="text-stone-600">Excelente trabalho!</p>
                </div>
                {fraseFimTarefa && (
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-green-100 shadow-sm">
                    <Quote className="h-6 w-6 text-[#E07A5F] mb-3 mx-auto opacity-70" />
                    <p className="text-lg italic text-stone-700 leading-relaxed">"{fraseFimTarefa}"</p>
                  </div>
                )}
                <Button
                  onClick={handleFecharSucesso}
                  className="w-full h-14 rounded-full bg-green-600 text-white hover:bg-green-700 text-lg font-semibold shadow-lg"
                  data-testid="btn-continuar-sucesso"
                >
                  Continuar
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog de Proximas Operacoes */}
          <Dialog open={showProximasOperacoes} onOpenChange={() => {}}>
            <DialogContent className="max-w-md mx-auto rounded-3xl border-0 shadow-2xl p-6 [&>button:last-child]:hidden" data-testid="proximas-operacoes-dialog-standalone">
              <div className="text-center space-y-4">
                <div className="relative mx-auto w-16 h-16">
                  <div className="relative flex items-center justify-center w-16 h-16 bg-green-500 rounded-full">
                    <CheckCircle2 className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-green-700 mb-1">Tarefa Concluida!</h2>
                  <p className="text-stone-600 text-sm">Selecione a proxima operacao para continuar:</p>
                </div>
                {ofConcluida && (
                  <div className="bg-stone-50 rounded-xl p-3 text-left">
                    <p className="font-bold" style={{ color: '#1E293B' }}>OF {ofConcluida.numero_of}</p>
                    <p className="text-xs text-stone-500">{ofConcluida.cliente} - {ofConcluida.artigo}</p>
                  </div>
                )}
                
                {/* Operações Congeladas — destaque visual */}
                {operacoesCongeladas.length > 0 && (
                  <>
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1 h-px bg-blue-200"></div>
                      <span className="text-xs text-blue-500 uppercase tracking-wider font-bold flex items-center gap-1">
                        <Snowflake className="h-3 w-3" /> Congelada
                      </span>
                      <div className="flex-1 h-px bg-blue-200"></div>
                    </div>
                    <div className="space-y-2">
                      {operacoesCongeladas.map((cong) => (
                        <button
                          key={cong.registo_id}
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/producao/descongelar`, { operador_id: user.id, registo_id: cong.registo_id });
                              setShowProximasOperacoes(false);
                              setProximasOperacoes([]);
                              setDadosConclusaoAnterior(null);
                              await loadProducaoAtiva();
                              await loadOperacoesCongeladas();
                              toast.success('Operacao retomada!');
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Erro ao retomar');
                              await loadOperacoesCongeladas();
                            }
                          }}
                          className="w-full border-2 border-blue-300 bg-blue-50 rounded-xl px-4 py-3 text-left hover:bg-blue-100 transition-colors flex items-center gap-3"
                          data-testid={`btn-retomar-congelada-${cong.registo_id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0">
                            <Snowflake className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm text-blue-900">{cong.operacao_codigo} {cong.operacao_nome}</p>
                            <p className="text-xs text-blue-600">{cong.lote_codigo} · {cong.of_numero}</p>
                          </div>
                          <span className="text-xs font-bold text-blue-700 bg-blue-200 px-2 py-1 rounded-full flex-shrink-0">Retomar</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-stone-200"></div>
                  <span className="text-xs text-stone-400 uppercase tracking-wider">Proximas tarefas</span>
                  <div className="flex-1 h-px bg-stone-200"></div>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {(() => {
                    const lotesMap = {};
                    proximasOperacoes.forEach(op => {
                      const loteKey = op.lote_id || 'sem-lote';
                      if (!lotesMap[loteKey]) {
                        lotesMap[loteKey] = { lote_id: op.lote_id, lote_codigo: op.lote_codigo || 'Sem lote', quantidade: op.lote_quantidade || 0, operacoes: [] };
                      }
                      lotesMap[loteKey].operacoes.push(op);
                    });
                    return Object.values(lotesMap).map((grupo, idx) => (
                      <div key={grupo.lote_id || idx} className="border border-stone-200 rounded-xl overflow-hidden">
                        <div className="bg-amber-50 px-3 py-2 border-b border-amber-100">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-amber-800">Lote {grupo.lote_codigo}</span>
                            <span className="text-xs text-amber-600">{grupo.quantidade} un.</span>
                          </div>
                          <span className="text-xs text-amber-600">{grupo.operacoes.length} operacao(oes) pendente(s)</span>
                        </div>
                        <div className="divide-y divide-stone-100">
                          {grupo.operacoes.map((op) => (
                            <button
                              key={`${op.operacao_id}-${op.lote_id || 'no-lote'}`}
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/producao/iniciar`, { of_id: op.of_id, lote_id: op.lote_id, operacao_id: op.operacao_id, operador_id: user.id });
                                  sessionStorage.setItem('producao_inicio_local', new Date().toISOString());
                                  setShowProximasOperacoes(false);
                                  setProximasOperacoes([]);
                                  setDadosConclusaoAnterior(null);
                                  await loadProducaoAtiva();
                                  toast.success('Nova operacao iniciada!');
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Erro ao iniciar operacao');
                                }
                              }}
                              className="w-full px-3 py-2.5 text-left hover:bg-stone-50 flex items-center gap-3 transition-colors"
                              data-testid={`btn-iniciar-proxima-${op.operacao_id}`}
                            >
                              <div className="flex-1">
                                <p className="font-semibold text-sm">{op.operacao_codigo} {op.operacao_nome}</p>
                                <p className="text-xs text-stone-400">{op.status === 'parcial' ? 'Parcial' : 'Pendente'}</p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-stone-300" />
                            </button>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowProximasOperacoes(false);
                    setDadosConclusaoAnterior(null);
                    if (isSimulation) { navigate(`/sim/operador/${username}`); } else { navigate('/operador'); }
                  }}
                  className="w-full"
                  data-testid="btn-voltar-home-standalone"
                >
                  Voltar ao inicio
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        </>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-sage border-t-transparent mx-auto mb-4"></div>
          <p className="text-stone-500">A carregar...</p>
        </div>
      </div>
    );
  }

  const { of, operacao, lote, registo } = producaoData;
  
  // Calcular quantidade restante (não produzida)
  // IMPORTANTE: Usar quantidade_ajustada se existir (após contagem), senão usar quantidade original
  const quantidadeLoteTotal = lote?.quantidade_ajustada || lote?.quantidade || of?.quantidade_total || 1;
  
  // Obter quantidade já produzida: priorizar operacoes_estado, depois contagem da última pausa, depois sessão
  const quantidadeOperacaoEstado = lote?.operacoes_estado?.find(
    op => op.operacao_id === operacao?.id
  )?.quantidade_produzida || 0;
  
  // Verificar se há contagem da última pausa (almoço/fim do dia)
  const ultimaPausa = registo?.pausas?.[registo.pausas.length - 1];
  const contagemUltimaPausa = ultimaPausa?.quantidade_parcial || 0;
  
  // A quantidade já produzida é o MAIOR entre: operacoes_estado + contagem da pausa actual
  const quantidadeJaProduzida = quantidadeOperacaoEstado + contagemUltimaPausa;
  
  const quantidadeRestante = Math.max(0, quantidadeLoteTotal - quantidadeJaProduzida);
  
  // Tempo standard = tempo da operação × quantidade RESTANTE (em segundos)
  const tempoStandardBase = operacao?.tempo_standard || 0; // tempo por peça em segundos
  const tempoStandard = tempoStandardBase * quantidadeRestante; // Tempo para completar o restante
  const percentualTempo = tempoStandard > 0 ? Math.min((tempoDecorrido / tempoStandard) * 100, 100) : 0;

  const handleBreakPause = (breakInfo) => {
    // Abrir drawer de pausa com motivo pré-selecionado
    setMotivoPausa(breakInfo.name);
    setShowPausaDrawer(true);
  };

  // ─── Banner de Pausa Programada (compacto no topo, NÃO bloqueia a tela) ──────
  const pausaBanner = (emPausaProgramada && emPausa && pausaProgramadaAtual && producaoData) ? (
    <div 
      className="sticky top-0 z-40 w-full"
      style={{ background: '#1E293B', borderBottom: '3px solid #F59E0B' }}
      data-testid="pausa-programada-banner"
    >
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Icone */}
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(251,191,36,0.2)' }}>
          <span className="text-2xl">{pausaProgramadaAtual.icon || '☕'}</span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-white">
            Pausa: {pausaProgramadaAtual.nome}
          </p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <Clock className="h-3 w-3 inline mr-1" />
            Retoma as {pausaProgramadaAtual.horaFim}
            <span className="ml-2 font-mono font-bold" style={{ color: '#FDE68A' }}>
              {formatTempo(tempoPausaAtual)}
            </span>
          </p>
        </div>

        {/* Botao retomar */}
        <button
          onClick={handleRetomar}
          disabled={loading}
          className="px-4 py-2 rounded-xl font-bold text-sm transition-all active:scale-95 flex-shrink-0"
          style={{ background: '#F59E0B', color: '#1E293B' }}
          data-testid="btn-retomar-pausa-programada"
        >
          <Play className="h-4 w-4 inline mr-1" />
          {loading ? 'A retomar...' : 'Retomar'}
        </button>
      </div>
    </div>
  ) : null;

  // (REMOVIDO: overlay full-screen de Pausa Programada que bloqueava a tela inteira)

  // ─── Ecrã de Conclusão (layout Samidel) — para TODAS as operações ───────────
  const operacaoAtual = producaoData?.operacao || null;
  // Lote ARR: usa regras de contagem como qualquer op com is_contagem.
  const isArrLote = producaoData?.lote?.categoria_codigo === 'ARR' || producaoData?.lote?.origem === 'arranjo';
  // Fallback: ler is_contagem do registo (campo is_operacao_contagem gravado ao iniciar)
  const isOperacaoContagem2 = operacaoAtual?.is_contagem || producaoData?.registo?.is_operacao_contagem || false;
  const isPontualCheck = operacaoAtual?.apenas_tarefas || producaoData?.registo?.is_tarefa_pontual || false;

  // Definir isContagemUnitaria aqui (antes dos early returns que dependem dela)
  const isContagem = operacaoAtual?.is_contagem || producaoData?.registo?.is_operacao_contagem || operacaoAtual?.tipo_contagem === 'unitaria' || false;
  const tipoContagem = operacaoAtual?.tipo_contagem || producaoData?.registo?.tipo_contagem || 'fim';
  const isContagemUnitaria = (isContagem && tipoContagem === 'unitaria') || tipoContagem === 'unitaria';

  // Usar CompleteTaskScreen tanto para contagem como para operações normais
  // Para tarefas pontuais, mostrar modal simplificado de quantidade
  if (showConcluirDrawer && producaoData && isPontualCheck) {
    // Tarefa pontual — modal simplificado de quantidade
    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#0B3B30' }}>
        {/* Header */}
        <div className="px-5 pt-6 pb-4">
          <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.6)' }}>TAREFA PONTUAL</p>
          <p className="text-3xl font-black text-white leading-none mt-1">
            {producaoData?.of?.numero_of || '—'}
          </p>
          <p className="text-base font-bold mt-2" style={{ color: 'rgba(255,255,255,0.85)' }}>
            {(producaoData?.lote?.categoria_codigo === 'ARR' || producaoData?.lote?.origem === 'arranjo') && producaoData?.lote?.descricao
              ? producaoData.lote.descricao
              : `${operacaoAtual?.codigo || ''} ${operacaoAtual?.nome || ''}`.trim()}
          </p>
          <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {producaoData?.of?.cliente || '—'} · {producaoData?.of?.artigo || '—'}
          </p>
        </div>

        {/* Conteúdo branco */}
        <div className="flex-1 px-5 pt-6 pb-6" style={{ background: '#FFFFFF', borderTopLeftRadius: '24px', borderTopRightRadius: '24px' }}>
          <h3 className="text-xl font-extrabold mb-2" style={{ color: '#1E293B' }}>
            Quantidade realizada
          </h3>
          <p className="text-sm mb-6" style={{ color: '#64748B' }}>
            Indique quantas unidades foram produzidas/realizadas nesta tarefa.
          </p>

          <input
            type="number"
            inputMode="numeric"
            min="0"
            value={pontualQty}
            onChange={e => setPontualQty(e.target.value)}
            placeholder="0"
            autoFocus
            className="w-full text-center font-black text-4xl py-4 rounded-2xl border-2 bg-white outline-none mb-6"
            style={{ borderColor: '#0B3B30', color: '#1E293B' }}
            data-testid="input-pontual-qty"
          />

          {/* Botões */}
          <div className="flex gap-3">
            <button
              onClick={() => { setShowConcluirDrawer(false); setPontualQty(''); }}
              className="flex-1 py-4 rounded-2xl font-extrabold text-lg border-2 bg-white transition-opacity active:opacity-70"
              style={{ color: '#1E293B', borderColor: '#0B3B30' }}
              data-testid="btn-cancelar-pontual"
            >
              Voltar
            </button>
            <button
              onClick={async () => {
                const qty = parseInt(pontualQty);
                if (!qty || qty <= 0) {
                  toast.error('Indique a quantidade realizada');
                  return;
                }
                setPontualQty('');
                setShowConcluirDrawer(false);
                await handleConcluirCompleto(qty, 0, 0);
              }}
              disabled={loading || !pontualQty || parseInt(pontualQty) <= 0}
              className="flex-1 py-4 rounded-2xl font-extrabold text-lg text-white transition-opacity active:opacity-70 disabled:opacity-40"
              style={{ background: loading ? '#94A3B8' : '#7C3AED' }}
              data-testid="btn-confirmar-pontual"
            >
              {loading ? 'A guardar...' : 'Concluir'}
            </button>
          </div>
        </div>

        {/* BottomTabBar */}
        <BottomTabBar
          activeTab="timer"
          onChange={(tab) => {
            if (tab === 'timer') return;
            setShowConcluirDrawer(false);
            setPontualQty('');
            const base = isSimulation ? `/sim/operador/${username}` : '/operador';
            isSimulation ? safeNavigate(base) : navigate(base);
          }}
          emProducao={true}
        />
      </div>
    );
  }
  
  if (showConcluirDrawer && producaoData && !isPontualCheck && !isContagemUnitaria) {
    // handleTabNavigate para navegação do BottomTabBar
    const navigateFromComplete = (tab) => {
      setShowConcluirDrawer(false);
      const base = isSimulation ? `/sim/operador/${username}` : '/operador';
      isSimulation ? safeNavigate(base) : navigate(base);
    };

    return (
      <CompleteTaskScreen
          producaoData={producaoData}
          lote={lote}
          operacao={operacaoAtual}
          modoConclusao={modoConclusao}
          contagemAtual={contagemAtualContagem}
          saving={loading}
          onSave={async (pecasBoas, anomaliaCount, tipoFecho) => {
            setQuantidadeProduzida(String(pecasBoas));
            setModoConclusao(tipoFecho);
            if (tipoFecho === 'parcial') {
              await handleConcluirParcial(pecasBoas, anomaliaCount);
            } else {
              // Calcular desvio: peças não contabilizadas = disponível - peças boas - anomalias
              const maxP = lote?.quantidade_ajustada || lote?.quantidade || 0;
              const jaF = lote?.operacoes_estado?.find(
                op => op.operacao_id === operacaoAtual?.id
              )?.quantidade_produzida || 0;
              const disp = Math.max(0, maxP - jaF);
              const desvio = Math.max(0, disp - pecasBoas - anomaliaCount);
              await handleConcluirCompleto(pecasBoas, anomaliaCount, desvio);
            }
          }}
          onCancel={() => setShowConcluirDrawer(false)}
          onSairSemContar={handleSairSemContar}
          onNavigate={navigateFromComplete}
        />
    );
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // ─── Ecrã de Contagem ───────────────────────────────────────────────────────
  // (isContagem, tipoContagem, isContagemUnitaria já definidos acima)
  const isPontual = operacaoAtual?.apenas_tarefas || producaoData?.registo?.is_tarefa_pontual || false;

  // Helper para navegação do BottomTabBar (permite navegar durante produção)
  // Se há operações congeladas, avisa o operador antes de sair
  const handleTabNavigate = (tab) => {
    if (operacoesCongeladas.length > 0) {
      setPendingNavigationTab(tab);
      setShowAvisoCongeladaDialog(true);
      return;
    }
    const base = isSimulation ? `/sim/operador/${username}` : '/operador';
    if (tab === 'inicio' || tab === 'operacoes' || tab === 'alertas' || tab === 'perfil') {
      isSimulation ? safeNavigate(base) : navigate(base);
    }
  };

  const executePendingNavigation = () => {
    setShowAvisoCongeladaDialog(false);
    const base = isSimulation ? `/sim/operador/${username}` : '/operador';
    const tab = pendingNavigationTab;
    setPendingNavigationTab(null);
    if (tab === 'inicio' || tab === 'operacoes' || tab === 'alertas' || tab === 'perfil') {
      isSimulation ? safeNavigate(base) : navigate(base);
    }
  };

  // ─── TimerScreen (operações normais + "Fechar no Fim") ──────────────────────
  if (!isContagemUnitaria && producaoData && !loading && !showConcluirDrawer) {
    return (
      <>
      {trocaOverlay}
      <TimerScreen
        producaoData={producaoData}
        lote={lote}
        operacao={operacaoAtual}
        tempoDecorrido={tempoDecorrido}
        isPaused={emPausa}
        isPontual={isPontual}
        onPause={() => setShowPausaDrawer(true)}
        onRetomar={() => handleRetomar && handleRetomar()}
        onConcluir={() => { setModoConclusao('concluir'); setShowConcluirDrawer(true); }}
        onParcial={isContagem ? null : (() => { setModoConclusao('parcial'); setShowConcluirDrawer(true); })}
        onCongelar={(operacoesCongeladas.length === 0 && !tarefaPontualCongelada) ? (() => setShowCongelarDialog(true)) : null}
        onBack={() => {
          if (isSimulation) {
            safeNavigate(`/sim/operador/${username}`);
          } else {
            navigate('/operador');
          }
        }}
        onNavigate={handleTabNavigate}
        onChamarGestor={handleChamarGestor}
        user={user}
      />
      {/* Pausa Drawer — overlaid on TimerScreen */}
      <Drawer open={showPausaDrawer} onOpenChange={setShowPausaDrawer}>
        <DrawerContent className="flex flex-col" style={{ borderRadius: '24px 24px 0 0', overflow: 'hidden', maxHeight: '80vh', marginBottom: '70px' }}>
          <div style={{ background: '#0B3B30' }} className="px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div>
              <DrawerTitle className="text-xl font-black text-white">Fazer Pausa</DrawerTitle>
              <DrawerDescription className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>
                Selecione o motivo da pausa
              </DrawerDescription>
            </div>
            <button
              onClick={() => setShowPausaDrawer(false)}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.2)' }}
              data-testid="close-pausa-drawer"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
          <div className="flex-1 p-4 space-y-2 overflow-y-auto min-h-0">
            {MOTIVOS_PAUSA.map((motivo) => {
              const isSelected = motivoPausa === motivo.label;
              return (
                <button
                  key={motivo.id}
                  onClick={() => {
                    setMotivoPausa(motivo.label);
                    if (!motivo.requerContagem) setContagemPausa('');
                  }}
                  className="w-full p-3 rounded-2xl text-left flex items-center gap-3 transition-all active:scale-98"
                  style={{
                    background: isSelected ? '#F0FDF4' : '#F8FAFC',
                    border: isSelected ? '2px solid #0B3B30' : '2px solid #E2E8F0',
                  }}
                  data-testid={`motivo-pausa-${motivo.id}`}
                >
                  <span className="text-2xl flex-shrink-0">{motivo.icon}</span>
                  <div className="flex-1">
                    <span className="font-extrabold text-sm block" style={{ color: '#1E293B' }}>{motivo.label}</span>
                    {motivo.pausaLegal && (
                      <span className="block text-[11px] mt-0.5" style={{ color: '#10B981' }}>
                        Pausa legal{motivo.horario ? ` · ${motivo.horario}` : ''}
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#0B3B30' }}>
                      <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
            {(() => {
              const motivoSelecionado = MOTIVOS_PAUSA.find(m => m.label === motivoPausa);
              if (motivoSelecionado?.requerContagem) {
                return (
                  <div className="rounded-2xl p-3 mt-2 border-2" style={{ background: '#FEF2F2', borderColor: '#FCA5A5' }}>
                    <label className="block text-sm font-extrabold mb-2" style={{ color: '#EF4444' }}>
                      Quantas peças produziu até agora? *
                    </label>
                    <input
                      type="number"
                      value={contagemPausa}
                      onChange={(e) => setContagemPausa(e.target.value)}
                      className="w-full p-3 rounded-xl border-2 text-lg font-bold text-center"
                      style={{ borderColor: '#FCA5A5' }}
                      placeholder="0"
                      min="0"
                      data-testid="contagem-pausa-input"
                    />
                  </div>
                );
              }
              return null;
            })()}
          </div>
          {/* Botões fixos no fundo do drawer — SEMPRE visíveis */}
          <div className="px-4 pb-4 pt-2 flex-shrink-0 space-y-2 border-t border-stone-100">
            <button
              onClick={() => handlePausa(motivoPausa)}
              disabled={loading || !motivoPausa || (MOTIVOS_PAUSA.find(m => m.label === motivoPausa)?.requerContagem && !contagemPausa)}
              className="w-full py-3 rounded-2xl font-extrabold text-base text-white transition-all active:scale-98 disabled:opacity-40"
              style={{ background: '#0B3B30' }}
              data-testid="confirmar-pausa-button"
            >
              {loading ? 'A registar...' : 'Confirmar Pausa'}
            </button>
            <button
              onClick={() => setShowPausaDrawer(false)}
              className="w-full py-2.5 rounded-2xl font-bold text-sm transition-all"
              style={{ color: '#64748B' }}
              data-testid="cancelar-pausa-button"
            >
              Voltar
            </button>
          </div>
        </DrawerContent>
      </Drawer>
      
      {/* Dialog Congelar — dentro do TimerScreen para estar acessível */}
      <Dialog open={showCongelarDialog} onOpenChange={setShowCongelarDialog}>
        <DialogContent className="max-w-md mx-auto rounded-3xl border-0 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Snowflake className="h-5 w-5" />
              Congelar Operação
            </DialogTitle>
            <DialogDescription>
              A operação será pausada e poderá iniciar outra tarefa. No fim do dia, deverá resolver todas as operações congeladas.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <div className="p-3 bg-blue-50 rounded-xl space-y-1">
              <p className="text-sm text-blue-700">
                <strong>Operação:</strong> {producaoData?.registo?.operacao_codigo} — {producaoData?.registo?.operacao_nome}
              </p>
              <p className="text-sm text-blue-700">
                <strong>OF:</strong> {producaoData?.registo?.of_numero}
              </p>
              <p className="text-sm text-blue-700">
                <strong>Tempo acumulado:</strong> {formatTempo(tempoDecorrido)}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowCongelarDialog(false)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleCongelar}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'A congelar...' : 'Congelar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Aviso — Operação Congelada ao tentar sair (TimerScreen) */}
      <Dialog open={showAvisoCongeladaDialog} onOpenChange={setShowAvisoCongeladaDialog}>
        <DialogContent className="max-w-md mx-auto rounded-3xl border-0 shadow-2xl z-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Operacao Congelada
            </DialogTitle>
            <DialogDescription>
              Tem uma operacao congelada pendente. Deve concluir ou registar parcial antes de sair.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            {operacoesCongeladas.map((op, idx) => (
              <div key={op.id || op.operacao_id || `${op.operacao_codigo}-${op.of_numero}-${idx}`} className="p-3 bg-amber-50 rounded-xl space-y-1 mb-2">
                <p className="text-sm text-amber-800 font-bold">
                  {op.operacao_codigo} — {op.operacao_nome}
                </p>
                <p className="text-xs text-amber-600">
                  OF: {op.of_numero}
                </p>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => executePendingNavigation()} className="flex-1">
              Sair mesmo assim
            </Button>
            <Button
              onClick={() => setShowAvisoCongeladaDialog(false)}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              Voltar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  if (isContagemUnitaria && producaoData) {
    return (
      <>
      {trocaOverlay}
      <div className="fixed inset-0 z-40" style={{ background: '#0B3B30' }}>
        <ContagemScreen
          producaoData={producaoData}
          lote={lote}
          operacao={operacaoAtual}
          tempoDecorrido={tempoDecorrido}
          tipoContagem={operacaoAtual?.tipo_contagem || 'fim'}
          isPaused={emPausa}
          initialCount={contagemAtualContagem}
          quantidadeJaProduzida={quantidadeJaProduzida}
          onRetomar={() => handleRetomar && handleRetomar()}
          onContagemChange={(val) => setContagemAtualContagem(val)}
          onFechar={(count, anomaliasCount) => {
            setQuantidadeProduzida(String(count));
            setContagemAtualContagem(count);
            setModoConclusao('concluir');
            setShowConcluirDrawer(true);
          }}
          onParcial={null}
          onPause={() => setShowPausaDrawer(true)}
          onCongelar={(operacoesCongeladas.length === 0 && !tarefaPontualCongelada) ? (() => setShowCongelarDialog(true)) : null}
          onSairSemContar={handleSairSemContar}
          onBack={() => {
            if (isSimulation) {
              safeNavigate(`/sim/operador/${username}`);
            } else {
              navigate('/operador');
            }
          }}
          onNavigate={handleTabNavigate}
          onChamarGestor={handleChamarGestor}
          user={user}
        />
      </div>

      {/* CompleteTaskScreen — overlay sobre o ContagemScreen (NÃO o desmonta) */}
      {showConcluirDrawer && (
        <div className="fixed inset-0 z-50">
          <CompleteTaskScreen
            producaoData={producaoData}
            lote={lote}
            operacao={operacaoAtual}
            modoConclusao={modoConclusao}
            contagemAtual={contagemAtualContagem}
            saving={loading}
            onSave={async (pecasBoas, anomaliaCount, tipoFecho) => {
              setQuantidadeProduzida(String(pecasBoas));
              setModoConclusao(tipoFecho);
              if (tipoFecho === 'parcial') {
                await handleConcluirParcial(pecasBoas, anomaliaCount);
              } else {
                const maxP = lote?.quantidade_ajustada || lote?.quantidade || 0;
                const jaFeitas = lote?.operacoes_estado?.find(
                  op => op.operacao_id === operacaoAtual?.id
                )?.quantidade_produzida || 0;
                const disponivel = Math.max(0, maxP - jaFeitas);
                const desvio = disponivel - parseInt(pecasBoas || 0) - parseInt(anomaliaCount || 0);
                setQuantidadeDesvio(desvio);
                await handleConcluirCompleto(pecasBoas, anomaliaCount, Math.max(0, desvio));
              }
            }}
            onCancel={() => setShowConcluirDrawer(false)}
            onSairSemContar={handleSairSemContar}
            onNavigate={(tab) => {
              setShowConcluirDrawer(false);
              const base = isSimulation ? `/sim/operador/${username}` : '/operador';
              isSimulation ? safeNavigate(base) : navigate(base);
            }}
          />
        </div>
      )}

      {/* Pausa Drawer — overlaid on ContagemScreen */}
      <Drawer open={showPausaDrawer} onOpenChange={setShowPausaDrawer}>
        <DrawerContent className="flex flex-col z-50" style={{ borderRadius: '24px 24px 0 0', overflow: 'hidden', maxHeight: '80vh', marginBottom: '70px' }}>
          <div style={{ background: '#0B3B30' }} className="px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div>
              <DrawerTitle className="text-xl font-black text-white">Fazer Pausa</DrawerTitle>
              <DrawerDescription className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>
                Selecione o motivo da pausa
              </DrawerDescription>
            </div>
            <button
              onClick={() => setShowPausaDrawer(false)}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.2)' }}
              data-testid="close-pausa-drawer-contagem"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
          <div className="flex-1 p-4 space-y-2 overflow-y-auto min-h-0">
            {MOTIVOS_PAUSA.map((motivo) => {
              const isSelected = motivoPausa === motivo.label;
              return (
                <button
                  key={motivo.id}
                  onClick={() => {
                    setMotivoPausa(motivo.label);
                    if (!motivo.requerContagem) setContagemPausa('');
                  }}
                  className="w-full p-3 rounded-2xl text-left flex items-center gap-3 transition-all active:scale-98"
                  style={{
                    background: isSelected ? '#F0FDF4' : '#F8FAFC',
                    border: isSelected ? '2px solid #0B3B30' : '2px solid #E2E8F0',
                  }}
                  data-testid={`motivo-pausa-contagem-${motivo.id}`}
                >
                  <span className="text-2xl flex-shrink-0">{motivo.icon}</span>
                  <div className="flex-1">
                    <span className="font-extrabold text-sm block" style={{ color: '#1E293B' }}>{motivo.label}</span>
                    {motivo.pausaLegal && (
                      <span className="block text-[11px] mt-0.5" style={{ color: '#10B981' }}>
                        Pausa legal{motivo.horario ? ` · ${motivo.horario}` : ''}
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#0B3B30' }}>
                      <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="px-4 pb-4 pt-2 flex-shrink-0 space-y-2 border-t border-stone-100">
            <button
              onClick={() => handlePausa(motivoPausa)}
              disabled={loading || !motivoPausa}
              className="w-full py-3 rounded-2xl font-extrabold text-base text-white transition-all active:scale-98 disabled:opacity-40"
              style={{ background: '#0B3B30' }}
              data-testid="confirmar-pausa-contagem-button"
            >
              {loading ? 'A registar...' : 'Confirmar Pausa'}
            </button>
            <button
              onClick={() => setShowPausaDrawer(false)}
              className="w-full py-2.5 rounded-2xl font-bold text-sm transition-all"
              style={{ color: '#64748B' }}
            >
              Voltar
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Dialog Congelar — acessível a partir da ContagemScreen */}
      <Dialog open={showCongelarDialog} onOpenChange={setShowCongelarDialog}>
        <DialogContent className="max-w-md mx-auto rounded-3xl border-0 shadow-2xl z-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Snowflake className="h-5 w-5" />
              Congelar Operação
            </DialogTitle>
            <DialogDescription>
              A operação será pausada e poderá iniciar outra tarefa. No fim do dia, deverá resolver todas as operações congeladas.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <div className="p-3 bg-blue-50 rounded-xl space-y-1">
              <p className="text-sm text-blue-700">
                <strong>Operação:</strong> {producaoData?.registo?.operacao_codigo} — {producaoData?.registo?.operacao_nome}
              </p>
              <p className="text-sm text-blue-700">
                <strong>OF:</strong> {producaoData?.registo?.of_numero}
              </p>
              <p className="text-sm text-blue-700">
                <strong>Tempo acumulado:</strong> {formatTempo(tempoDecorrido)}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowCongelarDialog(false)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleCongelar}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'A congelar...' : 'Congelar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Aviso — Operação Congelada ao tentar sair */}
      <Dialog open={showAvisoCongeladaDialog} onOpenChange={setShowAvisoCongeladaDialog}>
        <DialogContent className="max-w-md mx-auto rounded-3xl border-0 shadow-2xl z-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Operacao Congelada
            </DialogTitle>
            <DialogDescription>
              Tem uma operacao congelada pendente. Deve concluir ou registar parcial antes de sair.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            {operacoesCongeladas.map((op, idx) => (
              <div key={op.id || op.operacao_id || `${op.operacao_codigo}-${op.of_numero}-${idx}`} className="p-3 bg-amber-50 rounded-xl space-y-1 mb-2">
                <p className="text-sm text-amber-800 font-bold">
                  {op.operacao_codigo} — {op.operacao_nome}
                </p>
                <p className="text-xs text-amber-600">
                  OF: {op.of_numero}
                </p>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => executePendingNavigation()} className="flex-1">
              Sair mesmo assim
            </Button>
            <Button
              onClick={() => setShowAvisoCongeladaDialog(false)}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              Voltar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // ─── Fallback: redirecionar para TimerScreen se producaoData existe ───
  if (producaoData && !loading) {
    return (
      <TimerScreen
        producaoData={producaoData}
        lote={lote}
        operacao={operacaoAtual}
        tempoDecorrido={tempoDecorrido}
        isPaused={emPausa}
        isPontual={isPontual}
        onPause={() => setShowPausaDrawer(true)}
        onRetomar={() => handleRetomar && handleRetomar()}
        onConcluir={() => { setModoConclusao('concluir'); setShowConcluirDrawer(true); }}
        onParcial={isContagem ? null : (() => { setModoConclusao('parcial'); setShowConcluirDrawer(true); })}
        onCongelar={(operacoesCongeladas.length === 0 && !tarefaPontualCongelada) ? (() => setShowCongelarDialog(true)) : null}
        onBack={() => {
          if (isSimulation) {
            safeNavigate(`/sim/operador/${username}`);
          } else {
            navigate('/operador');
          }
        }}
        onNavigate={handleTabNavigate}
        onChamarGestor={handleChamarGestor}
        user={user}
      />
    );
  }

  // Se não há producaoData mas estamos na página de produção, mostrar carregamento
  // (evita flash da tela antiga durante transições)
  return (
    <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: '#F8FAFC' }}>
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-green-600 mb-4"></div>
      <p className="text-stone-500 text-sm">A carregar produção...</p>
      <button 
        onClick={() => {
          if (isSimulation) {
            safeNavigate(`/sim/operador/${username}`);
          } else {
            navigate('/operador');
          }
        }}
        className="mt-6 text-sm text-stone-400 underline"
      >
        Voltar ao início
      </button>
    </div>
  );
}

